// ─── App.jsx — Root Application Shell ────────────────────────────────────────
// Responsibilities:
//   • Wrap everything in <ThemeProvider>
//   • Handle auth (login / loading screens)
//   • Handle subscription gating
//   • Manage navigation (top pill bar)
//   • Manage multi-brand (dataKey) switching
//   • Compose all data from useData() and pass to pages

import React, { useState, useEffect, useCallback, useRef, useMemo, Suspense } from "react";
import { createPortal } from "react-dom";
import { ThemeProvider, useTheme, useThemeMode } from "./lib/ThemeContext.jsx";
import { trackPageView, trackEvent, identifyUser, setErrorUser } from "./lib/analytics.js";
import { useAuth, useData, supabase, SAVE_STATUS_LABELS, SAVE_STATUS_IS_ERROR } from "./hooks/useData.js";
import { FONT, RADIUS, SPRING } from "./lib/theme.js";
import BrandLogo from "./components/BrandLogo.jsx";
import { LS } from "./lib/storage.js";
import ReminderModal from "./components/ReminderModal.jsx";
import ContactUsModal from "./components/ContactUsModal.jsx";
// ── Reminder helpers (inline until reminders.js is in repo) ─────────────────
function getDueReminders(brandKey, now = new Date()) {
  try {
    const all = JSON.parse(localStorage.getItem("sf_reminders") || "[]");
    const acked = JSON.parse(localStorage.getItem("sf_reminders_acked") || "[]");
    return all.filter(r => {
      if (r.active === false) return false;
      if (acked.includes(r.id)) return false;
      const owns = r.ownBrand === brandKey || r.brandScope === "all" ||
        (Array.isArray(r.brandScope) && r.brandScope.includes(brandKey));
      if (!owns) return false;
      const today = now.toISOString().split("T")[0];
      const [hh, mm] = (r.time || "09:00").split(":").map(Number);
      const timeReached = now.getHours() > hh || (now.getHours() === hh && now.getMinutes() >= mm);
      if (r.type === "once") return r.date === today && timeReached;
      if (r.type === "daily") return (!r.date || today >= r.date) && timeReached;
      if (r.type === "weekly") return (!r.date || today >= r.date) && now.getDay() === (r.weekday ?? 1) && timeReached;
      if (r.type === "monthly") return now.getDate() === (r.dayOfMonth ?? 1) && timeReached;
      return false;
    });
  } catch { return []; }
}
function acknowledgeReminder(id) {
  try {
    const acked = JSON.parse(localStorage.getItem("sf_reminders_acked") || "[]");
    if (!acked.includes(id)) { acked.push(id); localStorage.setItem("sf_reminders_acked", JSON.stringify(acked)); }
  } catch {}
}
function fireBrowserNotif(title, body) {
  try {
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      new Notification(title, { body, icon: "/favicon.ico" });
    }
  } catch {}
}
import { computeAlerts, detectAppIssues } from "./lib/calculations.js";

// ─── Pages — Critical (main bundle) ──────────────────────────────────────────
import Dashboard      from "./pages/Dashboard.jsx";
import CashCalendar   from "./pages/CashCalendar.jsx";
import Targets        from "./pages/Targets.jsx";
import Marketplaces   from "./pages/Marketplaces.jsx";
import MarketplaceDataAudit from "./pages/MarketplaceDataAudit.jsx";
import OnboardingWizard from "./pages/Onboarding.jsx";
import Help, { HelpButton } from "./pages/Help.jsx";

// ─── Pages — Lazy loaded (separate chunks, loaded on first visit) ────────────
const lazyRetry = (fn) => React.lazy(() => fn().catch(() => { window.location.reload(); return fn(); }));
const Planner        = lazyRetry(() => import("./pages/Planner.jsx"));
const SupplierOrders = lazyRetry(() => import("./pages/expenses/SupplierOrdersSection.jsx").then(m => ({ default: m.SupplierOrdersSection })));
const Recurring      = lazyRetry(() => import("./pages/Recurring.jsx"));
const Modifiers      = lazyRetry(() => import("./pages/Modifiers.jsx"));
// Products = restored unit-economics page (formerly Profitability). The
// per-product cost editor inside Marketplaces.jsx is still around — this
// page sits above it as a standalone calculator users can iterate on.
const Products       = lazyRetry(() => import("./pages/Products.jsx"));
const Tasks          = lazyRetry(() => import("./pages/Tasks.jsx"));
const ExportFull     = lazyRetry(() => import("./pages/ExportFull.jsx"));
const Profile        = lazyRetry(() => import("./pages/Profile.jsx"));
const Settings       = lazyRetry(() => import("./pages/Settings.jsx"));
const Legal          = lazyRetry(() => import("./pages/Legal.jsx"));
const DataMap        = lazyRetry(() => import("./pages/DataMap.jsx"));
import ChatWidget     from "./components/ChatWidget.jsx";
import BackToTop      from "./components/BackToTop.jsx";
import PaywallTiers   from "./components/PaywallTiers.jsx";
import WhatsAppConnect from "./components/WhatsAppConnect.jsx";
import ErrorBoundary, { SectionBoundary }  from "./components/ErrorBoundary.jsx";
import ChangelogPanel from "./components/ChangelogPanel.jsx";
import { useRecentChanges } from "./hooks/useRecentChanges.js";
import CookieConsent  from "./components/CookieConsent.jsx";
import GuidedTour, { TourButton, TOURS } from "./components/GuidedTour.jsx";
import { useAccount } from "./hooks/useAccount.js";

// ─── Nav config ───────────────────────────────────────────────────────────────
// `hidden: true` keeps the page route + handler alive (URL hash still
// works, deep links still resolve) but removes it from the top nav. Use
// for pages we want accessible from other entry points (Help via the "?"
// icon, Export & Import via the user-icon dropdown) or for pages we're
// parking without deleting (Recurring → its buttons live in the Cash
// Calendar add-transaction popover now).
const NAV_ITEMS = [
  { id: "dashboard",    label: "Dashboard",              color:"#a78bfa", lines: ["Dashboard"],             emoji: "📊" },
  { id: "cashflow",     label: "Cash Calendar",          color:"#34d399", lines: ["Cash", "Calendar"],      emoji: "📅" },
  { id: "targets",      label: "Targets",                color:"#f97316", lines: ["Targets"],               emoji: "🎯" },
  { id: "planner",      label: "Planner",                color:"#60a5fa", lines: ["Planner"],               emoji: "🗺️" },
  { id: "marketplaces", label: "Markets",                color:"#fb923c", lines: ["Markets"],               emoji: "🛒" },
  { id: "catalog",      label: "Catalog",                color:"#10b981", lines: ["Catalog"],               emoji: "📚" },
  { id: "tasks",        label: "Tasks",                  color:"#f472b6", lines: ["Tasks"],                 emoji: "📝" },
  { id: "orders",       label: "Supplier Orders",        color:"#4ade80", lines: ["Supplier", "Orders"],    emoji: "📦" },
  { id: "transactions", label: "Recurring Transactions", color:"#e879f9", lines: ["Recurring", "Transactions"], emoji: "🔄", hidden: true },
  { id: "adjustments",  label: "Modifiers",              color:"#facc15", lines: ["Modifiers"],             emoji: "⚡" },
  { id: "export",       label: "Export & Import",        color:"#06b6d4", lines: ["Export", "& Import"],    emoji: "📤", hidden: true },
  { id: "help",         label: "Help",                   color:"#a78bfa", lines: ["Help"],                  emoji: "📖", hidden: true },
];

// ─── Login screen ─────────────────────────────────────────────────────────────
function LoginScreen() {
  const T = useTheme();
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [success, setSuccess]   = useState("");
  const [loading, setLoading]   = useState(false);
  // Detect invite link — auto-switch to signup mode and pre-fill email
  const inviteParams = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("invite") === "accept") {
        return { isInvite: true, email: params.get("email") || "" };
      }
    } catch {}
    return { isInvite: false, email: "" };
  }, []);
  // Detect Shopify install redirect — the app is being opened via a Shopify
  // Custom-Distribution install link, which lands at our root with
  // `?shop=<store>.myshopify.com&hmac=...&host=...` (and optionally
  // `session` / `embedded` / `timestamp`). Persist the shop domain in
  // sessionStorage so the context survives the email-confirmation round
  // trip, the Stripe-checkout redirect, and any in-app navigation between
  // signup and dashboard. Cleared by Root's follow-up redirect once the
  // seller lands on an active account.
  const shopifyInstall = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const shop = params.get("shop");
      const looksLikeInstall =
        shop && /\.myshopify\.com$/i.test(shop) &&
        (params.get("hmac") || params.get("host") ||
         params.get("session") || params.get("embedded"));
      if (looksLikeInstall) {
        sessionStorage.setItem("ch.shopifyInstall", shop);
        return { isInstall: true, shop };
      }
      const cached = sessionStorage.getItem("ch.shopifyInstall");
      if (cached) return { isInstall: true, shop: cached };
    } catch {}
    return { isInstall: false, shop: "" };
  }, []);
  const [mode, setMode]         = useState(() => {
    if (inviteParams.isInvite) return "signup";
    if (shopifyInstall.isInstall) return "signup";
    try {
      const p = new URLSearchParams(window.location.search).get("mode");
      if (p === "signup" || p === "login" || p === "forgot") return p;
    } catch {}
    return "login";
  }); // login | signup | forgot
  const [showPwd, setShowPwd]   = useState(false);

  // Pre-fill invited email
  useEffect(() => {
    if (inviteParams.isInvite && inviteParams.email && !email) {
      setEmail(inviteParams.email);
    }
  }, []);

  // Password strength
  const pwdStrength = useMemo(() => {
    if (!password || mode !== "signup") return null;
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    if (score <= 1) return { label:"Weak", color:"#ef4444", pct:20 };
    if (score <= 2) return { label:"Fair", color:"#f59e0b", pct:40 };
    if (score <= 3) return { label:"Good", color:"#f59e0b", pct:60 };
    if (score <= 4) return { label:"Strong", color:"#22c55e", pct:80 };
    return { label:"Very strong", color:"#22c55e", pct:100 };
  }, [password, mode]);

  async function handleSubmit() {
    setError(""); setSuccess(""); setLoading(true);
    try {
      if (mode === "forgot") {
        if (!email) { setError("Enter your email address"); setLoading(false); return; }
        const { error: e } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin,
        });
        if (e) setError(e.message);
        else setSuccess("Password reset link sent! Check your email (including spam folder).");
        setLoading(false);
        return;
      }
      let signupData = null;
      const { data: authData, error: err } =
        mode === "login"
          ? await supabase.auth.signInWithPassword({ email, password })
          : await supabase.auth.signUp({
              email, password,
              // Land confirmed users on a branded "Welcome" screen instead
              // of the bare app root. The trailing `#welcome=1` is detected
              // by the post-auth effect (see useEffect "fresh-confirmation").
              options: { emailRedirectTo: `${window.location.origin}/#welcome=1` },
            });
      if (mode === "signup") signupData = authData;
      if (err) {
        // Invited user who ALREADY has a CashHorizon account — auto-switch to
        // login mode and surface a clear message instead of the dead-end
        // "already registered" error.
        if (err.message.includes("already registered") && inviteParams.isInvite) {
          setMode("login");
          setPassword("");
          setError("");
          setSuccess(`This email already has a CashHorizon account. Please sign in below — we'll link this team invitation to your existing account automatically.`);
        }
        // Friendly error messages
        else if (err.message.includes("Invalid login")) setError("Incorrect email or password. Try again or reset your password.");
        else if (err.message.includes("already registered")) setError("This email is already registered. Try signing in instead.");
        else if (err.message.includes("valid email")) setError("Please enter a valid email address.");
        else if (err.message.includes("at least")) setError("Password must be at least 6 characters.");
        else setError(err.message);
      } else if (mode === "signup") {
        // When Supabase's "Confirm email" is OFF, signUp returns a session
        // immediately and the user is auto-logged-in — telling them to
        // "check your email" would be misleading. Only show the
        // verification prompt when no session came back (i.e. verification
        // is required).
        if (signupData?.session) {
          setSuccess("Account created! Logging you in…");
        } else {
          setSuccess("Account created! Check your email to verify your account.");
        }
      }
    } catch (e) {
      setError("Connection error. Please check your internet and try again.");
    } finally { setLoading(false); }
  }

  async function handleGoogle() {
    setError(""); setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo: window.location.origin },
      });
      if (error) setError(error.message);
    } catch { setError("Google sign-in is not available right now."); }
    finally { setLoading(false); }
  }

  function switchMode(m) { setMode(m); setError(""); setSuccess(""); }

  const inp = {
    background: T.surface, border: `1px solid ${T.border}`, borderRadius: RADIUS.md,
    color: T.text, fontFamily: FONT, fontSize: 15, outline: "none",
    padding: "12px 14px", width: "100%", boxSizing: "border-box",
    transition: "border-color 0.2s",
  };

  return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:FONT, padding:20 }}>
      <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:16, padding:"40px 36px", width:"100%", maxWidth:400, boxShadow:"0 20px 60px rgba(0,0,0,0.15)" }}>

        {/* Logo — animated, letterless */}
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:6 }}>
          <BrandLogo size={42} style={{ flexShrink:0, borderRadius:12, boxShadow:"0 4px 16px rgba(139,92,246,0.4)", display:"block" }} />
          <div>
            <div style={{ fontSize:22, fontWeight:800, color:T.text, letterSpacing:-0.5 }}>CashHorizon&#8482;</div>
            <div style={{ fontSize:11, color:T.textSub }}>Cashflow forecasting for sellers</div>
          </div>
        </div>

        {/* Invite banner */}
        {inviteParams.isInvite && (
          <div style={{ background:"rgba(139,92,246,0.1)", border:"1px solid rgba(139,92,246,0.3)", borderRadius:12, padding:"12px 16px", marginTop:16, textAlign:"center" }}>
            <div style={{ fontSize:14, fontWeight:700, color:"#a78bfa", marginBottom:4 }}>You've been invited to a team</div>
            <div style={{ fontSize:12, color:T.textSub }}>
              {mode==="signup"
                ? "Create an account to accept the invitation and start collaborating."
                : "Sign in with your existing account to accept the invitation."}
            </div>
          </div>
        )}

        {/* Shopify install banner — seller arrived via a Shopify Custom-
            Distribution install link. Shown instead of a generic login so
            the merchant understands why signup is required and which store
            will be connected. sessionStorage["ch.shopifyInstall"] already
            holds the shop domain; Root redirects them to /#marketplaces
            after signup + plan selection. */}
        {shopifyInstall.isInstall && !inviteParams.isInvite && (
          <div style={{
            background: "linear-gradient(135deg,rgba(149,196,103,0.12),rgba(139,92,246,0.08))",
            border: "1px solid rgba(149,196,103,0.35)",
            borderRadius: 12, padding: "12px 16px", marginTop: 16, textAlign: "center",
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#96d264", marginBottom: 4 }}>
              Connecting Shopify store · {shopifyInstall.shop}
            </div>
            <div style={{ fontSize: 12, color: T.textSub }}>
              {mode === "signup"
                ? "Create your CashHorizon account to finish connecting your store."
                : "Sign in with your existing account to finish connecting your store."}
            </div>
          </div>
        )}

        {/* Mode title */}
        <div style={{ fontSize:14, color:T.textSub, marginTop:20, marginBottom:20 }}>
          {mode==="login" ? "Welcome back — sign in to continue" :
           mode==="signup" ? (inviteParams.isInvite ? "Create your account to join the team" : "Create your free account") :
           "Reset your password"}
        </div>

        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {/* Email */}
          <div>
            <label style={{ fontSize:11, fontWeight:600, color:T.textSub, textTransform:"uppercase", letterSpacing:0.5, marginBottom:4, display:"block" }}>Email</label>
            <input style={inp} type="email" placeholder="you@example.com" value={email}
              onChange={e=>setEmail(e.target.value)} autoComplete="email" autoFocus
              onFocus={e=>e.target.style.borderColor=T.accent}
              onBlur={e=>e.target.style.borderColor=T.border}
              onKeyDown={e=>e.key==="Enter"&&(mode==="forgot"?handleSubmit():document.getElementById("sf-pwd")?.focus())}/>
          </div>

          {/* Password — hidden in forgot mode */}
          {mode !== "forgot" && (
            <div>
              <label style={{ fontSize:11, fontWeight:600, color:T.textSub, textTransform:"uppercase", letterSpacing:0.5, marginBottom:4, display:"block" }}>Password</label>
              <div style={{ position:"relative" }}>
                <input id="sf-pwd" style={{...inp, paddingRight:42}} type={showPwd?"text":"password"}
                  placeholder={mode==="signup"?"Min 8 characters":"Enter password"}
                  value={password} onChange={e=>setPassword(e.target.value)}
                  autoComplete={mode==="login"?"current-password":"new-password"}
                  onFocus={e=>e.target.style.borderColor=T.accent}
                  onBlur={e=>e.target.style.borderColor=T.border}
                  onKeyDown={e=>e.key==="Enter"&&handleSubmit()}/>
                <button onClick={()=>setShowPwd(!showPwd)} type="button"
                  style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)",
                    background:"none", border:"none", color:T.textSub, cursor:"pointer", fontSize:16, padding:4 }}
                  tabIndex={-1}>
                  {showPwd ? "🙈" : "👁"}
                </button>
              </div>
              {/* Password strength — signup only */}
              {mode==="signup" && password && pwdStrength && (
                <div style={{ marginTop:6 }}>
                  <div style={{ display:"flex", gap:3, marginBottom:3 }}>
                    {[1,2,3,4,5].map(i => (
                      <div key={i} style={{ flex:1, height:3, borderRadius:2,
                        background: i <= Math.ceil(pwdStrength.pct/20) ? pwdStrength.color : T.border,
                        transition:"background 0.2s" }}/>
                    ))}
                  </div>
                  <div style={{ fontSize:10, color:pwdStrength.color, fontWeight:600 }}>{pwdStrength.label}</div>
                </div>
              )}
            </div>
          )}

          {/* Error / Success messages */}
          {error && <div style={{ fontSize:12, color:T.red, padding:"10px 12px", background:T.red+"12", borderRadius:RADIUS.sm, lineHeight:1.5 }}>{error}</div>}
          {success && <div style={{ fontSize:12, color:"#22c55e", padding:"10px 12px", background:"#22c55e12", borderRadius:RADIUS.sm, lineHeight:1.5 }}>{success}</div>}

          {/* Submit button */}
          <button onClick={handleSubmit} disabled={loading || !email || (mode!=="forgot" && !password)}
            style={{
              background: loading ? T.border : "linear-gradient(135deg, #8b5cf6, #06b6d4)",
              color:"#fff", border:"none", borderRadius:RADIUS.md, padding:"13px 0",
              fontSize:15, fontWeight:700, fontFamily:FONT,
              cursor: loading ? "wait" : "pointer", opacity: loading ? 0.7 : 1,
              transition:"opacity 0.2s",
              boxShadow: loading ? "none" : "0 4px 16px rgba(139,92,246,0.3)",
            }}>
            {loading ? "Please wait…" :
             mode==="login" ? "Sign in" :
             mode==="signup" ? "Create account" :
             "Send reset link"}
          </button>

          {/* Divider */}
          {mode !== "forgot" && (
            <div style={{ display:"flex", alignItems:"center", gap:12, margin:"4px 0" }}>
              <div style={{ flex:1, height:1, background:T.border }}/>
              <span style={{ fontSize:11, color:T.textSub }}>or</span>
              <div style={{ flex:1, height:1, background:T.border }}/>
            </div>
          )}

          {/* Google OAuth */}
          {mode !== "forgot" && (
            <button onClick={handleGoogle} disabled={loading}
              style={{
                display:"flex", alignItems:"center", justifyContent:"center", gap:10,
                background:"transparent", border:`1px solid ${T.border}`, borderRadius:RADIUS.md,
                padding:"11px 0", fontSize:14, fontWeight:600, fontFamily:FONT,
                color:T.text, cursor:"pointer", transition:"all 0.15s",
              }}
              onMouseEnter={e=>e.currentTarget.style.borderColor=T.accent}
              onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              Continue with Google
            </button>
          )}
        </div>

        {/* Footer links */}
        <div style={{ marginTop:20, display:"flex", flexDirection:"column", gap:8, alignItems:"center" }}>
          {mode === "login" && (
            <>
              <button onClick={()=>switchMode("forgot")}
                style={{ background:"none", border:"none", color:T.textSub, cursor:"pointer", fontFamily:FONT, fontSize:12, padding:0 }}>
                Forgot your password?
              </button>
              <div style={{ fontSize:13, color:T.textSub }}>
                No account?{" "}
                <button onClick={()=>switchMode("signup")}
                  style={{ background:"none", border:"none", color:T.accent, cursor:"pointer", fontFamily:FONT, fontSize:13, fontWeight:600, padding:0 }}>
                  Sign up free
                </button>
              </div>
            </>
          )}
          {mode === "signup" && (
            <>
              <div style={{ fontSize:10, color:T.textSub, textAlign:"center", lineHeight:1.5 }}>
                By creating an account, you agree to our{" "}
                <a href="https://app.cashhorizon.io/#legal?tab=tos" target="_blank" rel="noopener noreferrer" style={{ color:T.accent, fontSize:10, fontFamily:FONT, textDecoration:"underline" }}>Terms of Service</a>
                {" "}and{" "}
                <a href="https://app.cashhorizon.io/#legal?tab=privacy" target="_blank" rel="noopener noreferrer" style={{ color:T.accent, fontSize:10, fontFamily:FONT, textDecoration:"underline" }}>Privacy Policy</a>
              </div>
              <div style={{ fontSize:13, color:T.textSub }}>
                Already have an account?{" "}
                <button onClick={()=>switchMode("login")}
                  style={{ background:"none", border:"none", color:T.accent, cursor:"pointer", fontFamily:FONT, fontSize:13, fontWeight:600, padding:0 }}>
                  Sign in
                </button>
              </div>
            </>
          )}
          {mode === "forgot" && (
            <div style={{ fontSize:13, color:T.textSub }}>
              Remember your password?{" "}
              <button onClick={()=>switchMode("login")}
                style={{ background:"none", border:"none", color:T.accent, cursor:"pointer", fontFamily:FONT, fontSize:13, fontWeight:600, padding:0 }}>
                Back to sign in
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Paywall screen ───────────────────────────────────────────────────────────
function PaywallScreen({ session }) {
  const T = useTheme();
  const [loading, setLoading] = useState(false);

  async function handleCheckout() {
    setLoading(true);
    try {
      const res = await fetch("/api/create-checkout", {
        method:"POST", headers:{ "Content-Type":"application/json", Authorization:`Bearer ${session.access_token}` },
      });
      const { url } = await res.json();
      if (url) window.location.href = url;
    } finally { setLoading(false); }
  }

  async function handlePortal() {
    setLoading(true);
    try {
      const res = await fetch("/api/billing-portal", {
        method:"POST", headers:{ "Content-Type":"application/json", Authorization:`Bearer ${session.access_token}` },
      });
      const { url } = await res.json();
      if (url) window.location.href = url;
    } finally { setLoading(false); }
  }

  return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:FONT }}>
      <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg, padding:"40px 36px", width:"100%", maxWidth:400, boxShadow:T.shadowLg, textAlign:"center" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10, marginBottom:8 }}>
          <BrandLogo size={32} />
          <div style={{ fontSize:26, fontWeight:800, color:T.text }}>CashHorizon&#8482;</div>
        </div>
        <div style={{ fontSize:14, color:T.textSub, marginBottom:28, lineHeight:1.6 }}>
          Your subscription is inactive. Upgrade to continue.
        </div>
        <button onClick={handleCheckout} disabled={loading}
          style={{ background:T.blue, color:"#fff", border:"none", borderRadius:RADIUS.sm, padding:"12px 28px", fontSize:15, fontWeight:700, fontFamily:FONT, cursor:"pointer", marginBottom:12, width:"100%" }}>
          {loading?"Please wait…":"Subscribe"}
        </button>
        <button onClick={handlePortal} disabled={loading}
          style={{ background:"transparent", color:T.textSub, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, padding:"10px 28px", fontSize:13, fontFamily:FONT, cursor:"pointer", width:"100%" }}>
          Manage billing
        </button>
        <button onClick={()=>supabase.auth.signOut()}
          style={{ background:"none", border:"none", color:T.textSub, fontSize:12, fontFamily:FONT, cursor:"pointer", marginTop:16 }}>
          Sign out
        </button>
      </div>
    </div>
  );
}

// ─── Full-screen loader ────────────────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div style={{ minHeight:"100vh", background:"#0d0f1a", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ position:"relative", width:72, height:72, display:"flex", alignItems:"center", justifyContent:"center" }}>
        <div style={{ width:64, height:64, borderRadius:18, background:"linear-gradient(135deg,#8b5cf6,#06b6d4)", boxShadow:"0 12px 40px rgba(139,92,246,0.5)" }}/>
        <div style={{ position:"absolute", inset:-4, borderRadius:22, border:"3px solid transparent", borderTopColor:"#8b5cf6", borderRightColor:"#06b6d4", animation:"chSpin 1.2s linear infinite" }}/>
        <div style={{ position:"absolute", inset:-9, borderRadius:27, border:"1.5px solid transparent", borderBottomColor:"#8b5cf6", borderLeftColor:"#06b6d4", opacity:0.35, animation:"chSpin 2.4s linear infinite reverse" }}/>
      </div>
      <style>{`@keyframes chSpin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ─── useIsMobile ──────────────────────────────────────────────────────────────
function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return mobile;
}

// ─── Page skeleton ─────────────────────────────────────────────────────────────
export function SkeletonBlock({ w = "100%", h = 18, radius = 8, style = {} }) {
  const T = useTheme();
  return (
    <div style={{
      width: w, height: h, borderRadius: radius,
      background: T.surfaceHi,
      backgroundImage: `linear-gradient(90deg, ${T.surfaceHi} 0%, ${T.border} 50%, ${T.surfaceHi} 100%)`,
      backgroundSize: "400px 100%",
      animation: "sfShimmer 1.4s ease-in-out infinite",
      flexShrink: 0,
      ...style,
    }}/>
  );
}

function PageSkeleton() {
  const T = useTheme();
  const cardStyle = { background: T.card, border: `1px solid ${T.border}`, borderRadius: 20, padding: 20 };
  const rowStyle  = { display: "flex", gap: 12 };
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16, animation:"sfPageIn 0.2s ease both" }}>
      <div style={{ ...rowStyle, flexWrap:"wrap" }}>
        {[140,130,130,130,110,120].map((w,i) => (
          <div key={i} style={{ ...cardStyle, flex:1, minWidth:w, display:"flex", flexDirection:"column", gap:10 }}>
            <SkeletonBlock w="60%" h={10}/>
            <SkeletonBlock w="80%" h={24} radius={6}/>
            <SkeletonBlock w="50%" h={9}/>
          </div>
        ))}
      </div>
      <div style={rowStyle}>
        {[80,140,120,90,90].map((w,i) => <SkeletonBlock key={i} w={w} h={30} radius={10}/>)}
      </div>
      <div style={{ ...cardStyle, height:"calc(100vh - 320px)", minHeight:280, display:"flex", flexDirection:"column", gap:12 }}>
        <SkeletonBlock w="30%" h={14}/>
        <div style={{ flex:1, display:"flex", alignItems:"flex-end", gap:3, paddingTop:8 }}>
          {Array.from({ length:24 }, (_,i) => {
            const h = 30 + Math.sin(i*0.7)*20 + Math.cos(i*1.3)*15 + 40;
            return <SkeletonBlock key={i} w="100%" h={`${h}%`} radius={4} style={{ flex:1 }}/>;
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Brand picker dropdown ────────────────────────────────────────────────────
// ── Brand icon choices ────────────────────────────────────────────────────────
const BRAND_ICONS = ["🏪","🏢","🏭","🛒","🏬","🏦","🎯","🚀","💼","🌐","⭐","🔑","💎","🎨","🔥","⚡","🌟","🏆","💡","🛍","📦","🤝","🌱","🔮","🎪","🏅","🦁","🐉","🌊","🎭"];

function BrandCreator({ T, onAdd }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("🏪");
  const [showPicker, setShowPicker] = useState(false);

  function submit() {
    if (!name.trim()) return;
    onAdd({ key: "brand_" + Date.now(), label: name.trim(), icon });
    setName(""); setIcon("🏪"); setOpen(false); setShowPicker(false);
  }

  if (!open) return (
    <button onClick={()=>setOpen(true)} style={{
      width:"100%", padding:"7px 12px", background:"transparent",
      border:`1px dashed ${T.border}`, borderRadius:RADIUS.md,
      color:T.textSub, fontFamily:FONT, fontSize:12, cursor:"pointer",
      display:"flex", alignItems:"center", justifyContent:"center", gap:6,
    }}>+ New Brand</button>
  );

  return (
    <div style={{ padding:"8px 10px", background:T.surface, borderRadius:RADIUS.md, border:`1px solid ${T.accent}40` }}>
      <div style={{ display:"flex", gap:6, alignItems:"center", marginBottom:6 }}>
        <button onClick={()=>setShowPicker(p=>!p)} style={{
          fontSize:18, padding:"3px 6px", borderRadius:RADIUS.sm,
          border:`1px solid ${showPicker?T.accent:T.border}`, background:"transparent",
          cursor:"pointer", outline:"none", flexShrink:0,
        }}>{icon}</button>
        <input autoFocus value={name} onChange={e=>setName(e.target.value)}
          placeholder="Brand name" onKeyDown={e=>{ if(e.key==="Enter") submit(); if(e.key==="Escape") setOpen(false); }}
          style={{ flex:1, background:"transparent", border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, color:T.text, fontSize:13, padding:"4px 8px", outline:"none", fontFamily:FONT }}/>
        <button onClick={submit} style={{ background:T.accent, border:"none", borderRadius:RADIUS.sm, color:"#fff", fontSize:12, padding:"4px 8px", cursor:"pointer", fontFamily:FONT }}>✓</button>
        <button onClick={()=>setOpen(false)} style={{ background:"transparent", border:"none", color:T.textSub, fontSize:13, cursor:"pointer" }}>✕</button>
      </div>
      {showPicker && (
        <div style={{ display:"flex", flexWrap:"wrap", gap:4, maxWidth:220 }}>
          {BRAND_ICONS.map(ic=>(
            <button key={ic} onClick={()=>{ setIcon(ic); setShowPicker(false); }}
              style={{ fontSize:18, padding:"3px", borderRadius:RADIUS.sm,
                border:`1px solid ${icon===ic?T.accent:T.border}`,
                background:icon===ic?T.accent+"22":"transparent",
                cursor:"pointer", outline:"none" }}>{ic}</button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Brand picker dropdown ────────────────────────────────────────────────────
const BRANDS_KEY = "sf_brands";
function getStoredBrands() {
  try {
    const raw = localStorage.getItem(BRANDS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {}
  return [{ key:"main", label:"Brand 1" }];
}
function saveStoredBrands(brands) {
  try { localStorage.setItem(BRANDS_KEY, JSON.stringify(brands)); } catch {}
}

function BrandPicker({ dataKey, onChange }) {
  const T = useTheme();
  const [open, setOpen] = useState(false);
  const [brands, setBrands] = useState(getStoredBrands);
  const [editingKey, setEditingKey] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [confirmDeleteKey, setConfirmDeleteKey] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function h(e) { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setEditingKey(null); setConfirmDeleteKey(null); } }
    document.addEventListener("mousedown", h, true);
    return () => document.removeEventListener("mousedown", h, true);
  }, [open]);

  function updateBrands(next) { setBrands(next); saveStoredBrands(next); }

  function addBrand() {
    const n = brands.length + 1;
    const key = "brand_" + Date.now();
    const next = [...brands, { key, label:`Brand ${n}` }];
    updateBrands(next);
  }

  function renameBrand(key, label) {
    updateBrands(brands.map(b => b.key === key ? { ...b, label } : b));
    setEditingKey(null);
  }

  function deleteBrand(key) {
    if (brands.length <= 1) return; // can't delete last brand
    const next = brands.filter(b => b.key !== key);
    updateBrands(next);
    if (dataKey === key) onChange(next[0].key);
    setConfirmDeleteKey(null);
  }

  const current = brands.find(b => b.key === dataKey) || brands[0];

  return (
    <div ref={ref} style={{ position:"relative" }}>
      <button onClick={() => setOpen(o=>!o)} title="Switch brand" style={{
        background: open ? T.accent+"18" : "transparent",
        border:`1px solid ${open ? T.accent+"40" : T.border}`,
        borderRadius:RADIUS.md, color: open ? T.accent : T.textSub,
        cursor:"pointer", fontFamily:FONT, fontSize:13, fontWeight:600,
        padding:"5px 10px", display:"flex", alignItems:"center", gap:5,
        transition:"all 0.15s ease",
      }}>
        <span style={{ fontSize:14 }}>🏢</span>
        <span style={{ maxWidth:72, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{current.label}</span>
        <span style={{ fontSize:9, opacity:0.6 }}>▼</span>
      </button>

      {open && (
        <div style={{
          position:"absolute", top:"calc(100% + 6px)", right:0, zIndex:500,
          background:T.card, border:`1px solid ${T.border}`,
          borderRadius:RADIUS.lg, boxShadow:T.shadowLg,
          minWidth:200, overflow:"hidden",
          animation:"sfFadeIn 0.15s ease both",
        }}>
          <div style={{ padding:"8px 12px 6px", fontSize:10, fontWeight:700, color:T.textSub, letterSpacing:0.8, textTransform:"uppercase" }}>Brands</div>

          {brands.map(b => {
            const active = b.key === dataKey;
            if (editingKey === b.key) return (
              <div key={b.key} style={{ padding:"6px 10px", display:"flex", gap:6, alignItems:"center" }}>
                <input autoFocus value={editVal} onChange={e=>setEditVal(e.target.value)}
                  onKeyDown={e=>{ if(e.key==="Enter") renameBrand(b.key,editVal.trim()||b.label); if(e.key==="Escape") setEditingKey(null); }}
                  style={{ flex:1, background:T.surface, border:`1px solid ${T.accent}`, borderRadius:RADIUS.sm, color:T.text, fontSize:13, padding:"4px 8px", outline:"none", fontFamily:FONT }}/>
                <button onClick={()=>renameBrand(b.key,editVal.trim()||b.label)} style={{ background:T.accent, border:"none", borderRadius:RADIUS.sm, color:"#fff", fontSize:11, padding:"4px 8px", cursor:"pointer" }}>✓</button>
              </div>
            );
            if (confirmDeleteKey === b.key) return (
              <div key={b.key} style={{ padding:"8px 12px", background:T.red+"12" }}>
                <div style={{ fontSize:11, color:T.red, marginBottom:6 }}>Delete "{b.label}"?</div>
                <div style={{ display:"flex", gap:6 }}>
                  <button onClick={()=>deleteBrand(b.key)} style={{ flex:1, background:T.red, border:"none", borderRadius:RADIUS.sm, color:"#fff", fontSize:11, padding:"4px 8px", cursor:"pointer", fontFamily:FONT }}>Delete</button>
                  <button onClick={()=>setConfirmDeleteKey(null)} style={{ flex:1, background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, color:T.textSub, fontSize:11, padding:"4px 8px", cursor:"pointer", fontFamily:FONT }}>Cancel</button>
                </div>
              </div>
            );
            return (
              <div key={b.key} style={{ display:"flex", alignItems:"center", padding:"2px 6px 2px 14px", gap:4,
                background: active ? T.accent+"14" : "transparent" }}>
                <button onClick={() => { onChange(b.key); setOpen(false); }} style={{
                  flex:1, textAlign:"left", padding:"7px 0",
                  background:"transparent", border:"none",
                  color: active ? T.accent : T.text,
                  fontFamily:FONT, fontSize:13, fontWeight:active?700:400, cursor:"pointer",
                  display:"flex", alignItems:"center", gap:6,
                }}>
                  {b.label}
                  {active && <span style={{ fontSize:11, color:T.accent }}>✓</span>}
                </button>
                <button onClick={()=>{ setEditingKey(b.key); setEditVal(b.label); }}
                  style={{ background:"none", border:"none", color:T.textSub, cursor:"pointer", fontSize:11, padding:"4px 5px", borderRadius:RADIUS.sm }}
                  title="Rename">✏</button>
                {brands.length > 1 && (
                  <button onClick={()=>setConfirmDeleteKey(b.key)}
                    style={{ background:"none", border:"none", color:T.textSub, cursor:"pointer", fontSize:11, padding:"4px 5px", borderRadius:RADIUS.sm }}
                    title="Delete brand">🗑</button>
                )}
              </div>
            );
          })}

          <div style={{ borderTop:`1px solid ${T.border}`, padding:"6px 10px" }}>
            <BrandCreator T={T} onAdd={b=>{const next=[...brands,b];updateBrands(next);}}/>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Theme picker dropdown ────────────────────────────────────────────────────
const THEME_OPTIONS = [
  { value:"light", icon:"☀️", label:"Light" },
  { value:"dark",  icon:"🌙", label:"Dark"  },
  { value:"auto",  icon:"⚡", label:"Auto"  },
];

function ThemePicker({ mode, onChange }) {
  const T = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", h, true);
    return () => document.removeEventListener("mousedown", h, true);
  }, [open]);

  const current = THEME_OPTIONS.find(t => t.value === mode) || THEME_OPTIONS[2];

  return (
    <div ref={ref} style={{ position:"relative" }}>
      <button onClick={() => setOpen(o=>!o)} title="Change theme" style={{
        background: open ? T.accent+"18" : "transparent",
        border:`1px solid ${open ? T.accent+"40" : T.border}`,
        borderRadius:RADIUS.md, color: open ? T.accent : T.textSub,
        cursor:"pointer", padding:"5px 9px", fontSize:15, lineHeight:1,
        transition:"all 0.15s ease",
      }}>
        {current.icon}
      </button>

      {open && (
        <div style={{
          position:"absolute", top:"calc(100% + 6px)", right:0, zIndex:500,
          background:T.card, border:`1px solid ${T.border}`,
          borderRadius:RADIUS.lg, boxShadow:T.shadowLg,
          minWidth:120, overflow:"hidden",
          animation:"sfFadeIn 0.15s ease both",
        }}>
          <div style={{ padding:"8px 12px 6px", fontSize:10, fontWeight:700, color:T.textSub, letterSpacing:0.8, textTransform:"uppercase" }}>Appearance</div>
          {THEME_OPTIONS.map(opt => {
            const active = opt.value === mode;
            return (
              <button key={opt.value} onClick={() => { onChange(opt.value); setOpen(false); }} style={{
                width:"100%", textAlign:"left", padding:"9px 14px",
                background: active ? T.accent+"14" : "transparent",
                border:"none", color: active ? T.accent : T.text,
                fontFamily:FONT, fontSize:13, fontWeight: active?700:400,
                cursor:"pointer", display:"flex", alignItems:"center", gap:8,
              }}>
                <span>{opt.icon}</span>
                <span style={{ flex:1 }}>{opt.label}</span>
                {active && <span style={{ fontSize:11, color:T.accent }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Top nav pill bar ─────────────────────────────────────────────────────────
function TopNav({ page, onNavigate, isMobile, themeMode, setThemeMode, dataKey, changeBrand, can }) {
  // Strip hidden entries first (Help/Recurring/Export are still reachable
  // via the "?" icon, the Calendar add-transaction popover, and the user
  // dropdown respectively — but should not clutter the top bar).
  let navItems = NAV_ITEMS.filter(i => !i.hidden);
  // Then drop Dashboard for users without permission. Other pages gate
  // themselves inline.
  if (can && !can("dashboard", "view")) navItems = navItems.filter(i => i.id !== "dashboard");
  const T = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setMobileMenuOpen(false); }
    document.addEventListener("mousedown", h, true);
    return () => document.removeEventListener("mousedown", h, true);
  }, [mobileMenuOpen]);

  // Mobile: show current page name as a dropdown trigger
  if (isMobile) {
    const current = navItems.find(n => n.id === page);
    return (
      <div ref={ref} style={{ position:"relative" }}>
        <button onClick={() => setMobileMenuOpen(o=>!o)} style={{
          background:T.accent+"18", border:`1px solid ${T.accent+"40"}`,
          borderRadius:RADIUS.md, color:T.accent,
          fontFamily:FONT, fontSize:13, fontWeight:700,
          padding:"6px 12px", cursor:"pointer",
          display:"flex", alignItems:"center", gap:6,
        }}>
          <span>☰</span>
          <span>{current?.label ?? "Menu"}</span>
          <span style={{ fontSize:9, opacity:0.6 }}>▼</span>
        </button>
        {mobileMenuOpen && (
          <div style={{
            position:"fixed", top:52, left:0, right:0, bottom:0, zIndex:400,
            background:T.card, borderBottom:`1px solid ${T.border}`,
            boxShadow:T.shadowLg, padding:"8px 12px",
            display:"flex", flexDirection:"column", gap:2,
            animation:"sfFadeIn 0.15s ease both",
            overflowY:"auto",
          }}>
            {/* Tap backdrop to close */}
            <div onClick={() => setMobileMenuOpen(false)} style={{ position:"fixed", top:52, left:0, right:0, bottom:0, zIndex:-1 }}/>
            {navItems.map(item => {
              const active = page === item.id;
              return (
                <button key={item.id} onClick={() => { onNavigate(item.id); setMobileMenuOpen(false); }} style={{
                  textAlign:"left", padding:"11px 14px",
                  background: active ? T.accent+"14" : "transparent",
                  border: active ? `1px solid ${T.accent+"30"}` : "1px solid transparent",
                  borderRadius:RADIUS.md,
                  color: active ? T.accent : T.text,
                  fontFamily:FONT, fontSize:14, fontWeight: active?700:400,
                  cursor:"pointer", display:"flex", alignItems:"center", gap:8,
                }}>
                  <span style={{ fontSize:16 }}>{item.emoji}</span>
                  {item.label}
                </button>
              );
            })}
            {/* Divider */}
            <div style={{ height:1, background:T.border, margin:"6px 0" }}/>
            {/* Theme toggle */}
            <button onClick={() => { setThemeMode?.(themeMode === "dark" ? "light" : "dark"); }} style={{
              textAlign:"left", padding:"11px 14px", background:"transparent",
              border:"1px solid transparent", borderRadius:RADIUS.md,
              color:T.textSub, fontFamily:FONT, fontSize:13, cursor:"pointer",
              display:"flex", alignItems:"center", gap:8,
            }}>
              {themeMode === "dark" ? "☀️" : "🌙"} {themeMode === "dark" ? "Light mode" : "Dark mode"}
            </button>
            {/* Brand switcher */}
            {getStoredBrands().length > 1 && getStoredBrands().map(b => (
              <button key={b.key} onClick={() => { changeBrand?.(b.key); setMobileMenuOpen(false); }} style={{
                textAlign:"left", padding:"8px 14px", background: b.key === dataKey ? T.accent+"14" : "transparent",
                border: b.key === dataKey ? `1px solid ${T.accent+"30"}` : "1px solid transparent",
                borderRadius:RADIUS.md, color: b.key === dataKey ? T.accent : T.textSub,
                fontFamily:FONT, fontSize:13, fontWeight: b.key === dataKey ? 700 : 400, cursor:"pointer",
                display:"flex", alignItems:"center", gap:8,
              }}>
                🏢 {b.label || b.key}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Desktop: horizontal pill tabs — icon above, text below
  return (
    <nav data-tour="nav-bar" aria-label="Main navigation" style={{
      display:"flex", alignItems:"stretch", gap:2,
      background:"transparent",
      borderRadius:RADIUS.xl,
      padding:"3px",
    }}>
      {navItems.map(item => {
        const active = page === item.id;
        return (
          <button key={item.id} onClick={() => onNavigate(item.id)} style={{
            padding:"5px 10px",
            borderRadius:RADIUS.lg,
            background: active ? `linear-gradient(135deg, ${item.color}28, ${item.color}12)` : "transparent",
            border: active ? `1px solid ${item.color}44` : "1px solid transparent",
            color: active ? (themeMode==="dark" ? item.color : T.text) : T.textSub,
            fontFamily:FONT,
            cursor:"pointer",
            transition:"background 0.3s ease, border-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease",
            display:"flex", flexDirection:"column", alignItems:"center", gap:2,
            minWidth:52,
          }}>
            <span style={{ fontSize:24, lineHeight:1, filter: active && themeMode==="dark" ? `drop-shadow(0 0 6px ${item.color}88)` : "none", transition:"filter 0.3s", display:"block" }}>{item.emoji}</span>
            <span style={{ fontSize:9, fontWeight: active?700:400, lineHeight:1.2, textAlign:"center", whiteSpace:"pre-line", color: active ? (themeMode!=="dark" && item.color==="#facc15" ? T.text : item.color) : T.text }}>
              {item.lines.join("\n")}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

// ─── Notification bell ────────────────────────────────────────────────────────
function NotificationBell({ data, navigate, isMobile, dataKey }) {
  const T = useTheme();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => new Set());
  const [dueReminders, setDueReminders] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", h, true);
    return () => document.removeEventListener("mousedown", h, true);
  }, [open]);

  // Check due reminders on mount and every minute
  useEffect(() => {
    function check() {
      const due = getDueReminders(dataKey);
      setDueReminders(due);
      due.forEach(r => fireBrowserNotif(r.title, r.message || "CashHorizon Reminder"));
    }
    check();
    const interval = setInterval(check, 60000);
    return () => clearInterval(interval);
  }, [dataKey]);

  const alerts = useMemo(() => {
    return computeAlerts({
      dailyData: [],
      availableToday: data.availableToday || 0,
      months: data.months || {},
      orders: data.orders || [],
      recurringExpenses: data.recurringExpenses || [],
      decisions: data.decisions || [],
      modifiers: data.modifiers || [],
      marketplaces: data.marketplaces || [],
      visibleYears: data.visibleYears || [new Date().getFullYear()],
      today: new Date(),
    });
  }, [data.months, data.orders, data.recurringExpenses, data.decisions, data.modifiers, data.marketplaces, data.visibleYears, data.availableToday]);

  // Config issues from detectAppIssues
  const configIssues = useMemo(() => {
    return detectAppIssues(
      data.marketplaces || [],
      data.months || {},
      data.orders || [],
      data.visibleYears || [new Date().getFullYear()],
    );
  }, [data.marketplaces, data.months, data.orders, data.visibleYears]);

  // Merge all notification sources, dedup by id
  const allNotifs = useMemo(() => {
    const items = [];
    const seen = new Set();
    // Alerts from computeAlerts
    for (const a of alerts) { if (!seen.has(a.id)) { seen.add(a.id); items.push(a); } }
    // Config issues from detectAppIssues
    for (const iss of configIssues) {
      if (!seen.has(iss.id)) {
        seen.add(iss.id);
        items.push({
          id: iss.id,
          severity: iss.severity === "error" ? "critical" : "warning",
          icon: iss.severity === "error" ? "🔴" : "🟡",
          title: iss.label,
          detail: iss.detail,
          page: iss.navPage || iss.page,
          action: iss.navPage ? `Open ${(iss.navPage||"").charAt(0).toUpperCase()+(iss.navPage||"").slice(1)}` : null,
        });
      }
    }
    // Due reminders
    for (const r of dueReminders) {
      const rid = "rem_" + r.id;
      if (!seen.has(rid)) {
        seen.add(rid);
        const at = r.attachedTo || {};
        let remPage = null, remAction = null;
        if (at.kind === "decision") { remPage = "planner"; remAction = "Open Planner"; }
        else if (at.kind === "transaction") { remPage = "cashflow"; remAction = "Open Calendar"; }
        else if (at.kind === "recurring") { remPage = "transactions"; remAction = "Open Recurring"; }
        items.push({
          id: rid, severity: "info", icon: "🔔",
          title: r.title, detail: r.message || "",
          page: remPage, action: remAction,
          reminderId: r.id,
        });
      }
    }
    return items;
  }, [alerts, configIssues, dueReminders]);

  const visible = allNotifs.filter(a => !dismissed.has(a.id));
  const critical = visible.filter(a => a.severity === "critical").length;
  const hasAny   = visible.length > 0;

  const badgeColor = critical > 0 ? T.red : T.orange;

  function dismiss(id) { setDismissed(prev => new Set([...prev, id])); }
  function dismissAll() { setDismissed(new Set(allNotifs.map(a => a.id))); }

  const severityColor = { critical: T.red, warning: T.orange, info: T.accent };

  return (
    <div ref={ref} style={{ position:"relative" }}>
      <button
        onClick={() => setOpen(o => !o)}
        title="Notifications"
        style={{
          position:"relative",
          background: open ? T.accent+"18" : "transparent",
          border:`1px solid ${open ? T.accent+"40" : T.border}`,
          borderRadius:RADIUS.md,
          color: open ? T.accent : T.textSub,
          cursor:"pointer", padding:"5px 8px",
          fontSize:15, lineHeight:1,
          transition:"all 0.15s ease",
        }}
      >
        🔔
        {hasAny && (
          <div style={{
            position:"absolute", top:-4, right:-4,
            width:15, height:15, borderRadius:"50%",
            background:badgeColor, color:"#fff",
            fontSize:8, fontWeight:900,
            display:"flex", alignItems:"center", justifyContent:"center",
            animation: critical > 0 ? "dotPulse 2s ease-in-out infinite" : "none",
          }}>
            {visible.length}
          </div>
        )}
      </button>

      {open && (
        <div style={{
          position: isMobile ? "fixed" : "absolute",
          top: isMobile ? 56 : "calc(100% + 8px)",
          right: isMobile ? 8 : 0,
          left: isMobile ? 8 : "auto",
          zIndex:9999,
          background:T.card, border:`1px solid ${T.border}`,
          borderRadius:RADIUS.lg, boxShadow:"0 20px 60px rgba(0,0,0,0.3)",
          width: isMobile ? "auto" : 380,
          maxHeight: isMobile ? "70vh" : 520, overflow:"auto",
          WebkitOverflowScrolling:"touch",
          animation:"sfFadeIn 0.15s ease both",
        }}>
          {/* Header */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 16px 10px", borderBottom:`1px solid ${T.border}` }}>
            <div style={{ fontSize:13, fontWeight:700, color:T.text }}>
              🔔 Notifications
              {hasAny && <span style={{ marginLeft:8, fontSize:11, color:T.textSub, fontWeight:400 }}>{visible.length} active</span>}
            </div>
            {hasAny && (
              <button onClick={dismissAll} style={{ background:"none", border:"none", color:T.textSub, fontSize:11, cursor:"pointer", fontFamily:FONT }}>
                Dismiss all
              </button>
            )}
          </div>

          {/* Alert list */}
          {visible.length === 0 ? (
            <div style={{ padding:"24px 16px", textAlign:"center" }}>
              <div style={{ fontSize:24, marginBottom:8 }}>✅</div>
              <div style={{ fontSize:13, fontWeight:600, color:T.text }}>All clear</div>
              <div style={{ fontSize:11, color:T.textSub, marginTop:4 }}>No active alerts right now</div>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column" }}>
              {visible.map(alert => {
                const color = severityColor[alert.severity] || T.textSub;
                return (
                  <div key={alert.id} style={{
                    display:"flex", alignItems:"flex-start", gap:10,
                    padding:"12px 16px",
                    borderBottom:`1px solid ${T.border}`,
                    borderLeft:`3px solid ${color}`,
                    background: color+"08",
                  }}>
                    <span style={{ fontSize:16, flexShrink:0, marginTop:1 }}>{alert.icon}</span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:700, color, lineHeight:1.3 }}>{alert.title}</div>
                      {alert.detail && (
                        <div style={{ fontSize:11, color:T.textSub, marginTop:3, lineHeight:1.4 }}>{alert.detail}</div>
                      )}
                      <div style={{ display:"flex", gap:6, marginTop:6 }}>
                        {alert.page && alert.action && (
                          <button onClick={() => { navigate(alert.page); setOpen(false); }} style={{
                            background:color+"18", border:`1px solid ${color}40`,
                            borderRadius:RADIUS.sm, color, fontFamily:FONT,
                            fontSize:10, fontWeight:700, padding:"3px 8px", cursor:"pointer",
                          }}>
                            {alert.action} →
                          </button>
                        )}
                        {alert.reminderId && (
                          <button onClick={() => { acknowledgeReminder(alert.reminderId); dismiss(alert.id); }} style={{
                            background:T.green+"18", border:`1px solid ${T.green}55`, color:T.green,
                            cursor:"pointer", fontSize:10, fontWeight:700, padding:"3px 8px",
                            borderRadius:RADIUS.sm, fontFamily:FONT,
                          }}>✓ Done</button>
                        )}
                      </div>
                    </div>
                    <button onClick={() => dismiss(alert.id)} title="Dismiss" style={{
                      background:"transparent", border:"none", color:T.textMuted,
                      cursor:"pointer", fontSize:13, padding:"0 2px", flexShrink:0, lineHeight:1,
                    }}>✕</button>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ padding:"8px 16px", fontSize:10, color:T.textMuted, borderTop:`1px solid ${T.border}` }}>
            Alerts refresh automatically
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Reusable full-page overlay (covers nav + everything) ─────────────────────
// Use this for any modal/form that needs to dim the entire screen.
export function FullOverlay({ open, onClose, children, zIndex = 10000 }) {
  useEffect(() => {
    if (open) {
      document.body.classList.add("sf-overlay-open");
    } else {
      document.body.classList.remove("sf-overlay-open");
    }
    return () => document.body.classList.remove("sf-overlay-open");
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div
      className="sf-full-overlay"
      style={{ zIndex }}
      onClick={e => e.target === e.currentTarget && onClose?.()}
    >
      {children}
    </div>,
    document.body
  );
}

// ─── Reusable right-side panel (covers nav + everything, slides in from right) ─
export function SidePanel({ open, onClose, children, width = 560, title }) {
  const T = useTheme();
  useEffect(() => {
    if (open) {
      document.body.classList.add("sf-overlay-open");
    } else {
      document.body.classList.remove("sf-overlay-open");
    }
    return () => document.body.classList.remove("sf-overlay-open");
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      style={{
        position:"fixed", inset:0, zIndex:10000,
        background:"rgba(0,0,0,0.55)",
        backdropFilter:"blur(16px)", WebkitBackdropFilter:"blur(16px)",
        display:"flex", alignItems:"stretch", justifyContent:"flex-end",
      }}
      onClick={e => e.target === e.currentTarget && onClose?.()}
    >
      <div style={{
        width: "100%", maxWidth: width,
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: "24px 0 0 24px",
        boxShadow: "-8px 0 48px rgba(0,0,0,0.35)",
        display:"flex", flexDirection:"column",
        animation:"sfSlideInFromRight 0.32s cubic-bezier(0.34,1.1,0.64,1) both",
        overflowY:"auto",
      }}>
        {children}
      </div>
    </div>,
    document.body
  );
}

// ─── Floating Calculator ─────────────────────────────────────────────────────
function FloatingCalculator() {
  const T = useTheme();
  const [open, setOpen] = useState(false);
  const [display, setDisplay] = useState("0");
  const [prev, setPrev] = useState(null);
  const [op, setOp] = useState(null);
  const [fresh, setFresh] = useState(true);

  function input(d) {
    if (fresh) { setDisplay(d); setFresh(false); }
    else setDisplay(display === "0" ? d : display + d);
  }
  function decimal() {
    if (fresh) { setDisplay("0."); setFresh(false); return; }
    if (!display.includes(".")) setDisplay(display + ".");
  }
  function operator(o) {
    const cur = parseFloat(display);
    if (prev !== null && op && !fresh) {
      const r = calc(prev, cur, op);
      setDisplay(String(r));
      setPrev(r);
    } else { setPrev(cur); }
    setOp(o); setFresh(true);
  }
  function calc(a, b, o) {
    if (o === "+") return a + b;
    if (o === "−") return a - b;
    if (o === "×") return a * b;
    if (o === "÷") return b !== 0 ? a / b : 0;
    return b;
  }
  function equals() {
    if (prev === null || !op) return;
    const cur = parseFloat(display);
    const r = calc(prev, cur, op);
    setDisplay(String(parseFloat(r.toFixed(10))));
    setPrev(null); setOp(null); setFresh(true);
  }
  function clear() { setDisplay("0"); setPrev(null); setOp(null); setFresh(true); }
  function percent() { setDisplay(String(parseFloat(display) / 100)); setFresh(true); }
  function toggleSign() { setDisplay(String(parseFloat(display) * -1)); }

  const btnBase = { border:`1px solid ${T.border}`, borderRadius:8, fontSize:16, fontWeight:600, cursor:"pointer", fontFamily:FONT, transition:"background 0.1s", padding:0, display:"flex", alignItems:"center", justifyContent:"center", height:40 };
  const numBtn = { ...btnBase, background:T.card, color:T.text };
  const opBtn = { ...btnBase, background:T.accent+"22", color:T.accent, border:`1px solid ${T.accent}33` };
  const fnBtn = { ...btnBase, background:T.card, color:T.textSub, fontSize:14 };

  return (
    <>
      {/* Floating button — hidden on mobile */}
      <button onClick={() => setOpen(o => !o)}
        style={{ position:"fixed", bottom:24, left:24, zIndex:8000, width:44, height:44, borderRadius:"50%",
          background:open?T.accent:T.card, border:`2px solid ${open?T.accent:T.border}`, color:open?"#fff":T.textSub,
          fontSize:18, cursor:"pointer", boxShadow:"0 4px 20px rgba(0,0,0,0.25)",
          display:"flex", alignItems:"center", justifyContent:"center", transition:"all 0.2s" }}
        className="sf-calc-btn"
        data-tour="calculator">
        🧮
      </button>

      {/* Calculator panel */}
      {open && (
        <div style={{ position:"fixed", bottom:76, left:24, zIndex:8001, width:240,
          background:T.card, border:`1px solid ${T.border}`, borderRadius:12,
          boxShadow:"0 20px 60px rgba(0,0,0,0.4)", padding:12, fontFamily:FONT,
          animation:"dashMenuIn 0.15s ease both" }}>
          {/* Display */}
          <div style={{ background:T.surface, borderRadius:8, padding:"10px 14px", marginBottom:10,
            fontSize:22, fontWeight:700, color:T.text, textAlign:"right", overflow:"hidden",
            textOverflow:"ellipsis", whiteSpace:"nowrap", minHeight:36, lineHeight:"36px" }}>
            {display}
          </div>
          {/* Buttons grid */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:6 }}>
            <button onClick={clear} style={fnBtn}>C</button>
            <button onClick={toggleSign} style={fnBtn}>±</button>
            <button onClick={percent} style={fnBtn}>%</button>
            <button onClick={()=>operator("÷")} style={{...opBtn,background:op==="÷"?T.accent:opBtn.background,color:op==="÷"?"#fff":opBtn.color}}>÷</button>

            <button onClick={()=>input("7")} style={numBtn}>7</button>
            <button onClick={()=>input("8")} style={numBtn}>8</button>
            <button onClick={()=>input("9")} style={numBtn}>9</button>
            <button onClick={()=>operator("×")} style={{...opBtn,background:op==="×"?T.accent:opBtn.background,color:op==="×"?"#fff":opBtn.color}}>×</button>

            <button onClick={()=>input("4")} style={numBtn}>4</button>
            <button onClick={()=>input("5")} style={numBtn}>5</button>
            <button onClick={()=>input("6")} style={numBtn}>6</button>
            <button onClick={()=>operator("−")} style={{...opBtn,background:op==="−"?T.accent:opBtn.background,color:op==="−"?"#fff":opBtn.color}}>−</button>

            <button onClick={()=>input("1")} style={numBtn}>1</button>
            <button onClick={()=>input("2")} style={numBtn}>2</button>
            <button onClick={()=>input("3")} style={numBtn}>3</button>
            <button onClick={()=>operator("+")} style={{...opBtn,background:op==="+"?T.accent:opBtn.background,color:op==="+"?"#fff":opBtn.color}}>+</button>

            <button onClick={()=>input("0")} style={{...numBtn,gridColumn:"span 2"}}>0</button>
            <button onClick={decimal} style={numBtn}>.</button>
            <button onClick={equals} style={{...btnBase,background:T.accent,color:"#fff",fontSize:18}}>=</button>
          </div>
        </div>
      )}

      {/* Hide on mobile */}
      <style>{`.sf-calc-btn{display:flex!important}@media(max-width:768px){.sf-calc-btn{display:none!important}}`}</style>
    </>
  );
}


function AppShell({ session, subscription }) {
  const T = useTheme();
  const { mode: themeMode, setMode: setThemeMode } = useThemeMode();
  useEffect(() => { document.body.classList.toggle("sf-light", themeMode === "light"); }, [themeMode]);
  const isMobile = useIsMobile();

  // ── Account resolution: find the data owner for shared access ──────────
  const {
    account, members, isOwner, permissions, can, featureAreas,
    dataOwnerId, accountLoading,
    inviteMember, acceptInvite, updateMemberPermissions, removeMember,
  } = useAccount(session);

  // ── Onboarding check — localStorage as primary, DB sync on complete ────
  const [onboarded, setOnboarded] = useState(() => {
    try { return localStorage.getItem("sf_onboarded") === "true"; } catch { return true; }
  });
  const [showOnboarding, setShowOnboarding] = useState(false);
  // Welcome screen: shown after wizard completes (first account creation)
  // OR when the user just confirmed their email (Supabase redirects them
  // back with `#welcome=1` per the emailRedirectTo on signup).
  const [showWelcome, setShowWelcome] = useState(false);

  // ── Changelog panel ────────────────────────────────────────────────────
  const [changelogOpen, setChangelogOpen] = useState(false);
  const changelogBtnRef = useRef(null);
  const { rows: changelogRows } = useRecentChanges();

  // ── fresh-confirmation: detect #welcome=1 once and trigger the welcome ──
  // The hash is stripped immediately so a refresh doesn't re-trigger it.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash.includes("welcome=1")) {
      setShowWelcome(true);
      try {
        const cleaned = window.location.hash
          .replace(/[#&]?welcome=1/, "")
          .replace(/^#&/, "#");
        window.history.replaceState(null, "", window.location.pathname + window.location.search + (cleaned === "#" ? "" : cleaned));
      } catch {}
    }
  }, []);
  // NOTE: onboarding effect moved below useData() call

  function getPageFromHash() {
    const hash = window.location.hash.replace("#", "").split("?")[0];
    return hash || "dashboard";
  }
  function getQueryFromHash() {
    const raw = window.location.hash.replace("#", "");
    const qIdx = raw.indexOf("?");
    if (qIdx === -1) return {};
    const out = {};
    for (const kv of raw.slice(qIdx + 1).split("&")) {
      if (!kv) continue;
      const [k, v = ""] = kv.split("=");
      try { out[decodeURIComponent(k)] = decodeURIComponent(v); }
      catch { out[k] = v; }
    }
    return out;
  }
  const [page, setPage]             = useState(getPageFromHash);
  const [pageParams, setPageParams] = useState({});
  const [hashQuery, setHashQuery]   = useState(getQueryFromHash);

  useEffect(() => {
    function onHashChange() {
      const p = getPageFromHash();
      setPage(p);
      setHashQuery(getQueryFromHash());
      trackPageView(p);
    }
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  // Identify user for analytics + error monitoring
  useEffect(() => {
    if (session?.user) {
      identifyUser(session.user.id, { email: session.user.email });
      setErrorUser(session.user.id, session.user.email);
    }
  }, [session?.user?.id]);

  const [dataKey, setDataKey] = useState(() => {
    const stored = LS.get(LS.BRAND, "main");
    // Migration: if the user was on the now-removed Atlas demo brand, drop them back to main
    if (typeof stored === "string" && stored.startsWith("demo_")) {
      LS.set(LS.BRAND, "main");
      return "main";
    }
    return stored;
  });

  // ── One-time cleanup: purge any remnants of the removed Atlas Outdoor demo brand ──
  useEffect(() => {
    try {
      // Clean sf_brands
      const raw = localStorage.getItem("sf_brands");
      if (raw) {
        let brands = [];
        try { brands = JSON.parse(raw); } catch {}
        if (Array.isArray(brands)) {
          const filtered = brands.filter(b => !(typeof b?.key === "string" && b.key.startsWith("demo_")));
          if (filtered.length !== brands.length) {
            localStorage.setItem("sf_brands", JSON.stringify(filtered.length ? filtered : [{ key:"main", label:"Brand 1" }]));
          }
        }
      }
      // Clean cache + version markers
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (!k) continue;
        if (k.startsWith("sf_cache_") && k.includes("_demo_")) localStorage.removeItem(k);
        if (k === "sf_demo_atlas_version") localStorage.removeItem(k);
      }
    } catch {}
  }, []);

  const navigate = useCallback((target, params = {}) => {
    window.location.hash = target;
    setPageParams(params || {});
  }, []);

  const changeBrand = useCallback((key) => {
    setDataKey(key);
    LS.set(LS.BRAND, key);
    setPage("dashboard");
    window.location.hash = "dashboard";
  }, []);

  // Pass dataOwnerId so invited members load the owner's data
  // When accountLoading, pass null to skip loading — useData falls back to session.user.id for owners
  const data = useData(session, dataKey, dataOwnerId);

  // Track if account is still loading (used in JSX, not as early return — hooks must always run)
  const accountReady = !accountLoading || !!dataOwnerId;

  // Expose currency/unit settings globally for calculations.js
  useEffect(() => {
    window.__sfCurrency   = data.currency;
    window.__sfUnitSystem = data.unitSystem;
  }, [data.currency, data.unitSystem]);

  // Onboarding — show wizard for new users. Once wizard is shown, NEVER close
  // it from this effect — only the wizard's own onComplete callback can close it.
  const wizardShownRef = useRef(false);
  useEffect(() => {
    if (accountLoading) return;
    if (wizardShownRef.current) return; // wizard already active — don't interfere
    if (!onboarded && isOwner) {
      if (data.dataLoading) {
        setShowOnboarding(true);
        wizardShownRef.current = true;
        return;
      }
      const hasProducts = (data.marketplaces || []).some(m => (m.products || []).length > 0);
      const hasOrders = (data.orders || []).length > 0;
      const hasRecurring = (data.recurringExpenses || []).length > 0 || (data.recurringIncome || []).length > 0;
      const hasTargets = Object.keys(data.months || {}).some(k => {
        const m = data.months[k];
        return (m?.target > 0) || (m?.expenses?.length > 0) || (m?.incomeItems?.length > 0);
      });
      const hasBalance = (data.availableToday || 0) > 0;
      const hasRealData = hasProducts || hasOrders || hasRecurring || hasTargets || hasBalance;
      if (hasRealData) {
        setOnboarded(true);
        try { localStorage.setItem("sf_onboarded", "true"); } catch {};
      } else {
        setShowOnboarding(true);
        wizardShownRef.current = true;
      }
    }
  }, [accountLoading, isOwner, onboarded, data.dataLoading]);

  const shared = {
    dataKey,
    // Permission helper: can("orders", "edit") → true/false
    can, isOwner, account,
    marketplaces:data.marketplaces, months:data.months,
    recurringIncome:data.recurringIncome, recurringExpenses:data.recurringExpenses,
    deliveryCreditLimit:data.deliveryCreditLimit, orders:data.orders,
    fbmSettings:data.fbmSettings, partners:data.partners,
    modifiers:data.modifiers, decisions:data.decisions, taxes:data.taxes,
    incomeOverrides:data.incomeOverrides, availableToday:data.availableToday,
    availableTodayHistory:data.availableTodayHistory,
    pastBalances:data.pastBalances,
    initialStock:data.initialStock, initialCostPerUnit:data.initialCostPerUnit,
    initialStockDate:data.initialStockDate, initialStockHistory:data.initialStockHistory,
    inventoryPools:data.inventoryPools, poolOps:data.poolOps,
    visibleYears:data.visibleYears,
    currency:data.currency, unitSystem:data.unitSystem,
    weekStartDay:data.weekStartDay, saveStatus:data.saveStatus,
    mktOps:data.mktOps, monthOps:data.monthOps, orderOps:data.orderOps,
    recurringIncomeOps:data.recurringIncomeOps,
    recurringExpenseOps:data.recurringExpenseOps,
    setDeliveryCreditLimit:data.setDeliveryCreditLimit,
    setFbmSettings:data.setFbmSettings, setPartners:data.setPartners,
    setModifiers:data.setModifiers, setDecisions:data.setDecisions,
    setTaxes:data.setTaxes,
    setIncomeOverride:data.setIncomeOverride,
    clearIncomeOverride:data.clearIncomeOverride,
    setAvailableToday:data.setAvailableToday,
    setVisibleYears:data.setVisibleYears,
    setCurrency:data.setCurrency, setUnitSystem:data.setUnitSystem,
    setWeekStartDay:data.setWeekStartDay,
    onNavigate:navigate,
    navContext:pageParams,
    onAddDecision:(d) => data.setDecisions([...(data.decisions||[]), d]),
    profitCards: data.profitCards,
    HelpButton, // Pass the HelpButton component so pages can render ? icons
    // Force a fresh reload of brand state from the server
    reloadData: data.reload,
    // Query params from the URL hash (e.g. { connected: "shopify" } after OAuth)
    hashQuery,
  };

  function renderPage() {
    // Pages use can("feature", "action") to check permissions
    // e.g. can("orders", "edit"), can("targets", "delete")

    switch (page) {
      case "dashboard": {
        if (!can("dashboard", "view")) {
          // Redirect to first allowed page (or Help as a safe last resort)
          const fallbacks = ["cashflow","targets","planner","marketplaces","orders","transactions","adjustments","export"];
          const target = fallbacks.find(p => {
            const k = p === "cashflow" ? "calendar" : p === "adjustments" ? "modifiers" : p;
            return can(k, "view");
          }) || "help";
          if (typeof window !== "undefined") { window.location.hash = target; }
          return null;
        }
        return <SectionBoundary name="Dashboard" resetKey={page}><Dashboard      {...shared}/></SectionBoundary>;
      }
      case "cashflow":     return <SectionBoundary name="Cash Calendar" resetKey={page}><CashCalendar   {...shared}/></SectionBoundary>;
      case "transactions": return <SectionBoundary name="Recurring Transactions" resetKey={page}><Recurring      {...shared}/></SectionBoundary>;
      case "targets":      return <SectionBoundary name="Targets" resetKey={page}><Targets        {...shared}/></SectionBoundary>;
      case "planner":      return <SectionBoundary name="Planner" resetKey={page}><Planner        {...shared} navContext={pageParams}/></SectionBoundary>;
      case "orders":       return <SectionBoundary name="Supplier Orders" resetKey={page}><SupplierOrders {...shared}/></SectionBoundary>;
      case "marketplaces": return <SectionBoundary name="Marketplaces" resetKey={page}><Marketplaces   {...shared}/></SectionBoundary>;
      case "marketplace-data": return <SectionBoundary name="Marketplace Data Audit" resetKey={page}><MarketplaceDataAudit marketplaces={data.marketplaces} months={data.months} hashQuery={hashQuery}/></SectionBoundary>;
      case "adjustments":  return <SectionBoundary name="Modifiers" resetKey={page}><Modifiers      {...shared}/></SectionBoundary>;
      case "catalog":      return <SectionBoundary name="Catalog" resetKey={page}><Products       profitCards={data.profitCards} setProfitCards={data.setProfitCards} marketplaces={data.marketplaces} mktOps={data.mktOps} navContext={pageParams} can={can} isOwner={isOwner} saveStatus={data.saveStatus}/></SectionBoundary>;
      case "tasks":        return <SectionBoundary name="Tasks" resetKey={page}><Tasks          session={session} can={can}/></SectionBoundary>;
      // Back-compat: stale links to the previous hashes ("profitability" /
      // "products") forward to the renamed Catalog page.
      case "profitability":
      case "products": { if (typeof window !== "undefined") window.location.hash = "catalog"; return null; }
      case "export":       return <SectionBoundary name="Export & Import" resetKey={page}><ExportFull     dataKey={dataKey} currency={data.currency}/></SectionBoundary>;
      case "help":         return <SectionBoundary name="Help" resetKey={page}><Help           onNavigate={navigate}/></SectionBoundary>;
      case "profile":      return <SectionBoundary name="Profile" resetKey={page}><Profile        session={session} account={account} members={members}
                                    isOwner={isOwner} can={can}
                                    inviteMember={inviteMember}
                                    updateMemberPermissions={updateMemberPermissions}
                                    removeMember={removeMember}
                                    navContext={pageParams}/></SectionBoundary>;
      // Legacy routes
      case "expenses":     return <SectionBoundary name="Supplier Orders" resetKey={page}><SupplierOrders {...shared}/></SectionBoundary>;
      case "monthly":      return <SectionBoundary name="Cash Calendar" resetKey={page}><CashCalendar   {...shared}/></SectionBoundary>;
      case "recurring":    return <SectionBoundary name="Recurring Transactions" resetKey={page}><Recurring      {...shared}/></SectionBoundary>;
      case "modifiers":    return <SectionBoundary name="Modifiers" resetKey={page}><Modifiers      {...shared}/></SectionBoundary>;
      case "settings":     return (
        <Settings
          currency={data.currency} unitSystem={data.unitSystem}
          setCurrency={data.setCurrency} setUnitSystem={data.setUnitSystem}
          visibleYears={data.visibleYears} setVisibleYears={data.setVisibleYears}
          weekStartDay={data.weekStartDay} setWeekStartDay={data.setWeekStartDay}
          saveStatus={data.saveStatus}
          deliveryCreditLimit={data.deliveryCreditLimit}
          setDeliveryCreditLimit={data.setDeliveryCreditLimit}
          months={data.months} monthOps={data.monthOps}
          session={session} subscription={subscription}
          dataKey={dataKey} onChangeBrand={changeBrand}
          account={account} isOwner={isOwner} can={can}
        />
      );
      case "legal":        return <Legal navContext={pageParams}/>;
      case "datamap":      return <DataMap/>;
      default: {
        // 404 — unknown route
        const T_ = T; // capture theme for inline render
        const knownPages = ["dashboard","cashflow","targets","planner","marketplaces","orders","transactions","adjustments","export","help","profile","settings","legal"];
        if (knownPages.includes(page)) return <Dashboard {...shared}/>;
        return (
          <div style={{ textAlign:"center", padding:"80px 20px", fontFamily:FONT }}>
            <div style={{ fontSize:56, marginBottom:16 }}>🔍</div>
            <div style={{ fontSize:24, fontWeight:800, color:T_.text, marginBottom:8 }}>Page not found</div>
            <div style={{ fontSize:14, color:T_.textSub, marginBottom:28 }}>The page "{page}" doesn't exist.</div>
            <button onClick={() => navigate("dashboard")} style={{
              background:"linear-gradient(135deg,#8b5cf6,#06b6d4)", color:"#fff", border:"none",
              borderRadius:12, padding:"13px 28px", fontSize:15, fontWeight:700, fontFamily:FONT,
              cursor:"pointer", boxShadow:"0 8px 24px rgba(139,92,246,0.35)",
            }}>Go to Dashboard</button>
          </div>
        );
      }
    }
  }

  const [profileOpen,    setProfileOpen]   = useState(false);
  const [contactOpen,    setContactOpen]   = useState(false);

  // ── Pause heavy CSS animations when the tab is hidden ──
  // Laptops were running fans at full speed because 6 full-viewport blurred
  // blobs were animating 24/7 even when the user wasn't looking at the page.
  // We toggle a body class that pauses every infinite animation we own.
  useEffect(() => {
    function onVis() {
      document.body.classList.toggle("sf-page-hidden", document.hidden);
    }
    document.addEventListener("visibilitychange", onVis);
    onVis();
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);
  const [showNavReminder, setShowNavReminder] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [profilePic, setProfilePic] = useState(() => LS.get(LS.PROFILE_PIC, ""));
  const profileRef = useRef(null);

  // Guided tour is manual-only — user clicks the Tour button in navbar to start.
  // Auto-start on every page was disabled because it interrupted the UX flow.

  // Profile dropdown close on outside click
  useEffect(() => {
    if (!profileOpen) return;
    function h(e) { if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false); }
    document.addEventListener("mousedown", h, true);
    return () => document.removeEventListener("mousedown", h, true);
  }, [profileOpen]);

  if (!accountReady) return (
    <div className="sf-root" style={{ display:"flex", flexDirection:"column", height:"100dvh", overflow:"hidden", background:T.bg, fontFamily:FONT, position:"relative" }}>
      <LoadingScreen/>
    </div>
  );

  return (
    <div className="sf-root" style={{ display:"flex", flexDirection:"column", height:"100dvh", overflow:"hidden", background:T.bg, fontFamily:FONT, position:"relative" }}>

      {/* iOS viewport fix + global mobile styles */}
      <style>{`
        @supports not (height:100dvh){.sf-root{height:100vh!important}}
        @media(max-width:768px){
          .sf-root{-webkit-overflow-scrolling:touch}
          main{-webkit-overflow-scrolling:touch}
        }
      `}</style>

      {/* Animated morphing background */}
      <div style={{ position:"fixed", inset:0, pointerEvents:"none", zIndex:0, overflow:"hidden" }}>
        <div className="sf-blob sf-blob-1"/>
        <div className="sf-blob sf-blob-2"/>
        <div className="sf-blob sf-blob-3"/>
        <div className="sf-blob sf-blob-4"/>
        <div className="sf-blob sf-blob-5"/>
        <div className="sf-blob sf-blob-6"/>
      </div>

      {/* ── Top bar ── */}
      <header style={{
        display:"flex", alignItems:"center", gap:8,
        padding: isMobile ? "0 12px" : "0 16px",
        height: isMobile ? 52 : 68, flexShrink:0,
        background:"transparent",
        position:"sticky", top:0, zIndex:200,
      }}>

        {/* Logo — animated gradient square + dual counter-rotating rings,
            matching LoadingScreen / PaywallTiers. Click → Dashboard. 28 px
            outer (~25% smaller than the previous 36 px CH block). No text. */}
        <div onClick={()=>navigate("dashboard")} title="Dashboard" style={{
          position:"relative", width:28, height:28, flexShrink:0, cursor:"pointer",
          display:"flex", alignItems:"center", justifyContent:"center",
        }}>
          <div style={{
            width:24, height:24, borderRadius:7,
            background:"linear-gradient(135deg,#8b5cf6,#06b6d4)",
            boxShadow:"0 4px 12px rgba(139,92,246,0.45)",
          }}/>
          <div style={{
            position:"absolute", inset:-2, borderRadius:9,
            border:"1.5px solid transparent",
            borderTopColor:"#8b5cf6", borderRightColor:"#06b6d4",
            animation:"sfSpin 1.2s linear infinite",
            pointerEvents:"none",
          }}/>
          <div style={{
            position:"absolute", inset:-5, borderRadius:12,
            border:"1px solid transparent",
            borderBottomColor:"#8b5cf6", borderLeftColor:"#06b6d4",
            opacity:0.35,
            animation:"sfSpin 2.4s linear infinite reverse",
            pointerEvents:"none",
          }}/>
        </div>

        {/* Divider */}
        {!isMobile && <div style={{ width:1, height:28, background:T.border, flexShrink:0, marginLeft:4 }}/>}

        {/* Nav pills — centered, takes all remaining space */}
        <div style={{ flex:1, display:"flex", justifyContent: isMobile ? "flex-start" : "center", overflow:"hidden", minWidth:0 }}>
          <TopNav page={page} onNavigate={navigate} isMobile={isMobile} themeMode={themeMode} setThemeMode={setThemeMode} dataKey={dataKey} changeBrand={changeBrand} can={can}/>
        </div>

        {/* Right controls */}
        <div style={{ display:"flex", alignItems:"center", gap: isMobile ? 4 : 5, flexShrink:0 }}>

          {/* Save status dot — shows the specific failure reason in the tooltip
              so users can tell "session expired" from "rate limited" etc. */}
          {(() => {
            const isErr = SAVE_STATUS_IS_ERROR(data.saveStatus);
            const isSaving = data.saveStatus === "saving";
            const dotColor = isSaving ? T.orange : isErr ? T.red : T.green;
            const labelColor = isSaving ? T.orange : isErr ? T.red : T.textMuted;
            const fullLabel = SAVE_STATUS_LABELS[data.saveStatus] || "Saved";
            const shortLabel = isSaving ? "Saving…" : isErr ? "Error" : "Saved";
            return (
              <div title={fullLabel} style={{ display:"flex", alignItems:"center", gap:3, fontSize:10, color: labelColor, width: isMobile ? 8 : 58, flexShrink:0, justifyContent:"flex-end" }}>
                <div style={{ width:5, height:5, borderRadius:"50%", flexShrink:0, background: dotColor }}/>
                {!isMobile && <span style={{ fontSize:10, whiteSpace:"nowrap" }}>{shortLabel}</span>}
              </div>
            );
          })()}

          {/* Loading spinner — fixed slot */}
          <div style={{ width:12, height:12, flexShrink:0 }}>
            {data.dataLoading && (
              <div style={{ width:12, height:12, border:`2px solid ${T.border}`, borderTopColor:T.accent, borderRadius:"50%", animation:"sfSpin 0.7s linear infinite" }}/>
            )}
          </div>

          {/* Desktop-only controls */}
          {!isMobile && (() => {
            // Unread changelog badge — count rows newer than last-seen timestamp
            let unread = 0;
            try {
              const lastSeen = localStorage.getItem("sf_changelog_last_seen") || "";
              if (lastSeen && changelogRows.length) {
                unread = changelogRows.filter(r => r.created_at > lastSeen).length;
              } else if (changelogRows.length) {
                unread = changelogRows.length;
              }
            } catch {}
            return (
              <button
                ref={changelogBtnRef}
                onClick={() => setChangelogOpen(o => !o)}
                title="Activity Log"
                style={{
                  position: "relative",
                  background: changelogOpen ? T.accent + "22" : "transparent",
                  border: `1px solid ${changelogOpen ? T.accent : T.border}`,
                  borderRadius: RADIUS.md,
                  color: changelogOpen ? T.accent : T.textSub,
                  cursor: "pointer", padding: "5px 8px", fontSize: 14, lineHeight: 1,
                  fontFamily: FONT,
                  transition: "all 0.15s ease",
                }}>
                📋
                {unread > 0 && (
                  <span style={{
                    position: "absolute", top: -4, right: -4,
                    minWidth: 16, height: 16, borderRadius: 8,
                    background: T.red, color: "#fff",
                    fontSize: 9, fontWeight: 800,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "0 4px",
                  }}>{unread > 99 ? "99+" : unread}</span>
                )}
              </button>
            );
          })()}
          {!isMobile && <ThemePicker mode={themeMode} onChange={setThemeMode}/>}
          {!isMobile && <HelpButton section={page} onNavigate={navigate}/>}
          {!isMobile && TOURS[page] && <TourButton onClick={() => setShowTour(true)}/>}
          {!isMobile && (
            <button
              onClick={() => setShowNavReminder(true)}
              title="Add a reminder"
              style={{
                background:"transparent",
                border:`1px solid ${T.border}`,
                borderRadius:RADIUS.md,
                color:T.textSub,
                cursor:"pointer",
                padding:"5px 8px",
                fontSize:14,
                lineHeight:1,
                transition:"all 0.15s ease",
              }}
              onMouseEnter={e=>{e.currentTarget.style.background=T.accent+"18";e.currentTarget.style.borderColor=T.accent+"40";e.currentTarget.style.color=T.accent;}}
              onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.borderColor=T.border;e.currentTarget.style.color=T.textSub;}}
            >
              🔔<span style={{ fontSize:10, fontWeight:900, marginLeft:2, verticalAlign:"super" }}>+</span>
            </button>
          )}

          {/* Bell (notification panel) — always visible */}
          <NotificationBell data={data} navigate={navigate} isMobile={isMobile} dataKey={dataKey}/>

          {/* Brand picker — desktop only in header, mobile gets it in hamburger */}
          {!isMobile && <BrandPicker dataKey={dataKey} onChange={changeBrand}/>}

          {/* Profile dropdown */}
          <div ref={profileRef} style={{ position:"relative" }}>
            <button onClick={() => setProfileOpen(o=>!o)} title="Profile & account" style={{
              width:32, height:32, borderRadius:"50%",
              background: profileOpen ? T.accent+"18" : T.surfaceHi,
              border:`1px solid ${profileOpen ? T.accent+"40" : T.border}`,
              cursor:"pointer", fontSize:16, lineHeight:1,
              display:"flex", alignItems:"center", justifyContent:"center",
              transition:"all 0.15s ease",
            }}>{profilePic
                ? <img src={profilePic} alt="" style={{width:22,height:22,borderRadius:"50%",objectFit:"cover"}}/>
                : "👤"
              }</button>

            {profileOpen && (
              <div style={{
                position:"absolute", top:"calc(100% + 8px)", right:0, zIndex:500,
                background:T.card, border:`1px solid ${T.border}`,
                borderRadius:RADIUS.lg, boxShadow:T.shadowLg,
                minWidth:200, overflow:"hidden",
                animation:"sfFadeIn 0.15s ease both",
              }}>
                <div style={{ padding:"14px 16px 10px", borderBottom:`1px solid ${T.border}` }}>
                  <div style={{ fontSize:12, fontWeight:700, color:T.text, marginBottom:2 }}>Account</div>
                  <div style={{ fontSize:11, color:T.textSub, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{session.user.email}</div>
                  {subscription && (
                    <div style={{ marginTop:6, fontSize:11, fontWeight:600, color: subscription.status==="active"||subscription.status==="trialing" ? T.green : T.red }}>
                      {subscription.status==="trialing" ? "Trial" : subscription.status==="active" ? "Active" : "Inactive"}
                    </div>
                  )}
                </div>
                {/* Profile picture upload */}
                <label style={{ display:"block", cursor:"pointer" }}>
                  <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display:"none" }}
                    onChange={e=>{
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 500000) { e.target.value=""; return; }
                      const reader = new FileReader();
                      reader.onload = ev => { setProfilePic(ev.target.result); LS.set(LS.PROFILE_PIC, ev.target.result); };
                      reader.readAsDataURL(file);
                      setProfileOpen(false);
                    }}/>
                  <div style={{ padding:"10px 16px", display:"flex", alignItems:"center", gap:8, color:T.text, fontSize:13 }}
                    onMouseEnter={e=>e.currentTarget.style.background=T.surfaceHi} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    🖼 {profilePic ? "Change Photo" : "Upload Photo"}
                  </div>
                </label>
                {profilePic && (
                  <button onClick={()=>{ setProfilePic(""); LS.set(LS.PROFILE_PIC,""); setProfileOpen(false); }} style={{
                    width:"100%", textAlign:"left", padding:"8px 16px",
                    background:"transparent", border:"none", color:T.textSub,
                    fontFamily:FONT, fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", gap:8,
                  }} onMouseEnter={e=>e.currentTarget.style.background=T.surfaceHi} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    ✕ Remove Photo
                  </button>
                )}
                <button onClick={() => { navigate("profile", { tab: "team" }); setProfileOpen(false); }} style={{
                  width:"100%", textAlign:"left", padding:"10px 16px",
                  background:"transparent", border:"none", color:T.text,
                  fontFamily:FONT, fontSize:13, cursor:"pointer",
                  display:"flex", alignItems:"center", gap:8,
                }} onMouseEnter={e=>e.currentTarget.style.background=T.surfaceHi} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  👥 Team
                </button>
                <button onClick={() => { navigate("profile", { tab: "connect" }); setProfileOpen(false); }} style={{
                  width:"100%", textAlign:"left", padding:"10px 16px",
                  background:"transparent", border:"none", color:T.text,
                  fontFamily:FONT, fontSize:13, cursor:"pointer",
                  display:"flex", alignItems:"center", gap:8,
                }} onMouseEnter={e=>e.currentTarget.style.background=T.surfaceHi} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  🤖 Connect AI
                </button>
                <button onClick={() => { navigate("profile", { tab: "settings" }); setProfileOpen(false); }} style={{
                  width:"100%", textAlign:"left", padding:"10px 16px",
                  background:"transparent", border:"none", color:T.text,
                  fontFamily:FONT, fontSize:13, cursor:"pointer",
                  display:"flex", alignItems:"center", gap:8,
                }} onMouseEnter={e=>e.currentTarget.style.background=T.surfaceHi} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  🔔 Notifications
                </button>
                <button onClick={() => { navigate("settings"); setProfileOpen(false); }} style={{
                  width:"100%", textAlign:"left", padding:"10px 16px",
                  background:"transparent", border:"none", color:T.text,
                  fontFamily:FONT, fontSize:13, cursor:"pointer",
                  display:"flex", alignItems:"center", gap:8,
                }} onMouseEnter={e=>e.currentTarget.style.background=T.surfaceHi} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  ⚙️ Settings
                </button>
                <button onClick={() => { navigate("export"); setProfileOpen(false); }} style={{
                  width:"100%", textAlign:"left", padding:"10px 16px",
                  background:"transparent", border:"none", color:T.text,
                  fontFamily:FONT, fontSize:13, cursor:"pointer",
                  display:"flex", alignItems:"center", gap:8,
                }} onMouseEnter={e=>e.currentTarget.style.background=T.surfaceHi} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  📤 Export & Import
                </button>
                <button onClick={async () => {
                  setProfileOpen(false);
                  try {
                    const res = await fetch("/api/billing-portal", {
                      method:"POST",
                      headers:{ "Content-Type":"application/json", Authorization:`Bearer ${session.access_token}` },
                    });
                    const { url } = await res.json();
                    if (url) window.open(url, "_blank");
                  } catch(e) { console.error("Portal error:", e); }
                }} style={{
                  width:"100%", textAlign:"left", padding:"10px 16px",
                  background:"transparent", border:"none", color:T.text,
                  fontFamily:FONT, fontSize:13, cursor:"pointer",
                  display:"flex", alignItems:"center", gap:8,
                }} onMouseEnter={e=>e.currentTarget.style.background=T.surfaceHi} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  💳 Manage Subscription
                </button>
                <button onClick={() => { setContactOpen(true); setProfileOpen(false); }} style={{
                  width:"100%", textAlign:"left", padding:"10px 16px",
                  background:"transparent", border:"none", color:T.text,
                  fontFamily:FONT, fontSize:13, cursor:"pointer",
                  display:"flex", alignItems:"center", gap:8,
                }} onMouseEnter={e=>e.currentTarget.style.background=T.surfaceHi} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  ✉️ Contact us
                </button>
                <button onClick={() => { navigate("legal"); setProfileOpen(false); }} style={{
                  width:"100%", textAlign:"left", padding:"10px 16px",
                  background:"transparent", border:"none", color:T.textSub,
                  fontFamily:FONT, fontSize:13, cursor:"pointer",
                  display:"flex", alignItems:"center", gap:8,
                }} onMouseEnter={e=>e.currentTarget.style.background=T.surfaceHi} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  📜 Legal & Privacy
                </button>
                <button onClick={() => supabase.auth.signOut()} style={{
                  width:"100%", textAlign:"left", padding:"10px 16px",
                  background:"transparent", border:"none", color:T.red,
                  fontFamily:FONT, fontSize:13, cursor:"pointer",
                  display:"flex", alignItems:"center", gap:8,
                  borderTop:`1px solid ${T.border}`,
                }} onMouseEnter={e=>e.currentTarget.style.background=T.red+"10"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  ⇥ Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Page content — full width ── */}
      <a href="#main-content" className="skip-to-content">Skip to content</a>
      <main id="main-content" role="main" style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column", minWidth:0, position:"relative", zIndex:1, WebkitOverflowScrolling:"touch" }}>
        <div style={{
          flex:1,
          padding: page==="planner" ? 0 : (isMobile ? "12px 10px 100px" : "20px 24px 100px"),
          minHeight:0,
          overflowY: page==="planner" ? "hidden" : "auto",
          height:    page==="planner" ? "100%" : undefined,
          WebkitOverflowScrolling:"touch",
        }}>
          {data.dataLoading ? (
            <PageSkeleton/>
          ) : data.loadError ? (
            // Fetch failed and we have no fresh data to show. Render a
            // retry card instead of letting empty defaults masquerade as
            // the user's real data.
            <div style={{
              maxWidth: 460, margin: "80px auto", padding: "32px 28px",
              background: T.card, border: `1px solid ${T.border}`,
              borderRadius: 18, textAlign: "center", fontFamily: FONT,
            }}>
              <div style={{ fontSize: 40, marginBottom: 10, opacity: 0.7 }}>⚠️</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: T.text, marginBottom: 6 }}>
                Couldn't load your data
              </div>
              <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.5, marginBottom: 20 }}>
                We hit a network or server error while fetching your brand state. Your data is safe — this is a transient issue.
              </div>
              <button
                onClick={() => data.reload()}
                style={{
                  background: "linear-gradient(135deg,#8b5cf6,#06b6d4)",
                  color: "#fff", border: "none", borderRadius: 10,
                  padding: "11px 26px", fontSize: 14, fontWeight: 700,
                  cursor: "pointer", fontFamily: FONT,
                  boxShadow: "0 6px 20px rgba(139,92,246,0.35)",
                }}
              >Retry</button>
              {data.loadError?.message && (
                <div style={{ fontSize: 10, color: T.textSub, opacity: 0.6, marginTop: 14, fontFamily: "monospace" }}>
                  {data.loadError.message}
                </div>
              )}
            </div>
          ) : (
            <Suspense fallback={<PageSkeleton/>}>
              <div style={{ animation:"sfPageIn 0.2s ease both" }}>{renderPage()}</div>
            </Suspense>
          )}
        </div>
      </main>

      {/* ── Onboarding wizard — renders as fixed overlay, independent of data loading ── */}
      {showOnboarding && isOwner && (
        <OnboardingWizard session={session} onComplete={() => {
          setShowOnboarding(false); setOnboarded(true);
          setShowWelcome(true);
          try { localStorage.setItem("sf_onboarded", "true"); } catch {}
        }} setAvailableToday={data.setAvailableToday}
          setCurrency={data.setCurrency} currency={data.currency}/>
      )}

      {/* ── Welcome screen — after onboarding or for returning users ── */}
      {showWelcome && <WelcomeScreen onClose={() => setShowWelcome(false)} />}

      {/* ── Floating Calculator — hidden on mobile ── */}
      <FloatingCalculator/>

      {/* ── AI Chat widget ── */}
      <SectionBoundary name="Chat" variant="widget"><ChatWidget userId={dataOwnerId || session.user.id} brandKey={dataKey}/></SectionBoundary>

      {/* ── Back-to-top button — appears after scrolling, sits above chat ── */}
      {/* Back-to-top hidden on the Markets page — the page is short
          enough that scrolling rarely needs a jump, and the button
          covered the bottom-right marketplace card. */}
      {page !== "marketplaces" && <BackToTop/>}

      {/* ── Changelog side-panel ── */}
      <ChangelogPanel
        open={changelogOpen}
        onClose={() => setChangelogOpen(false)}
        triggerRef={changelogBtnRef}
        onNavigate={navigate}
        session={session}
      />

      {/* Global reminder creator from navbar — wrapped safely */}
      {/* Contact Us modal — opened from the user dropdown */}
      <ContactUsModal open={contactOpen} onClose={() => setContactOpen(false)} session={session} account={account}/>

      {showNavReminder && (() => {
        try {
          return (
            <ReminderModal
              open={showNavReminder}
              onClose={() => setShowNavReminder(false)}
              globalMode
              data={{
                recurringExpenses: data.recurringExpenses || [],
                recurringIncome: data.recurringIncome || [],
                decisions: data.decisions || [],
                orders: data.orders || [],
              }}
              dataKey={dataKey}
            />
          );
        } catch (e) {
          console.error("ReminderModal render error:", e);
          return null;
        }
      })()}


      {/* ── Guided Tour overlay ── */}
      <SectionBoundary name="Tour" variant="widget"><GuidedTour steps={TOURS[page] || []} active={showTour} onClose={() => setShowTour(false)}/></SectionBoundary>

      <style>{`
        @keyframes sfSpin    { to { transform:rotate(360deg) } }
        @keyframes sfPageIn  { from { opacity:0; transform:translateY(6px) } to { opacity:1; transform:translateY(0) } }
        @keyframes sfShimmer { 0% { background-position:-400px 0 } 100% { background-position:400px 0 } }
        @keyframes sfFadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes sfSlideIn { from { opacity:0; transform:translateX(16px) } to { opacity:1; transform:translateX(0) } }
        @keyframes sfSlideInRight { from { opacity:0; transform:translateX(120px) scale(0.9) } to { opacity:1; transform:translateX(0) scale(1) } }
        @keyframes sfSlideInFromRight { from { opacity:0; transform:translateX(100%) } to { opacity:1; transform:translateX(0) } }
        @keyframes sfModalIn { from { opacity:0; transform:scale(0.88) translateY(16px) } to { opacity:1; transform:scale(1) translateY(0) } }
        @keyframes dotPulse  { 0%,100% { transform:scale(1) } 50% { transform:scale(1.3) } }
        @keyframes sfDigitUp  { from { opacity:0; transform:translateY(18px) } to { opacity:1; transform:translateY(0) } }
        @keyframes sfSpringIn { from { opacity:0; transform:scale(0.8) } to { opacity:1; transform:scale(1) } }

        /* ── Force dark mode for native date/time pickers ── */
        input[type="date"],
        input[type="time"],
        input[type="datetime-local"],
        input[type="month"],
        input[type="week"],
        select {
          color-scheme: dark;
        }
        input[type="date"]::-webkit-calendar-picker-indicator,
        input[type="time"]::-webkit-calendar-picker-indicator,
        input[type="datetime-local"]::-webkit-calendar-picker-indicator,
        input[type="month"]::-webkit-calendar-picker-indicator {
          filter: invert(0.85) hue-rotate(180deg);
          cursor: pointer;
        }

        /* ── Full-page overlay system ──
           Overlays use z-index:10000 via createPortal — they cover everything
           including the nav at z:200 without needing to drop the header z-index */
        .sf-full-overlay {
          position: fixed !important;
          inset: 0 !important;
          z-index: 10000 !important;
          backdrop-filter: blur(16px) !important;
          -webkit-backdrop-filter: blur(16px) !important;
          background: rgba(0,0,0,0.55) !important;
        }
        /* Side panels */
        .sf-side-panel {
          position: fixed !important;
          top: 0 !important; right: 0 !important; bottom: 0 !important;
          z-index: 10001 !important;
          display: flex !important;
          flex-direction: column !important;
          overflow-y: auto !important;
        }

        /* ── Morphing background blobs — full screen roaming ── */
        @keyframes sfBlob1 {
          0%   { transform: translate(0,0) scale(1) rotate(0deg); }
          20%  { transform: translate(45vw, 30vh) scale(1.3) rotate(72deg); }
          40%  { transform: translate(-20vw, 60vh) scale(0.8) rotate(144deg); }
          60%  { transform: translate(70vw, -10vh) scale(1.4) rotate(216deg); }
          80%  { transform: translate(-35vw, 80vh) scale(1.1) rotate(288deg); }
          100% { transform: translate(0,0) scale(1) rotate(360deg); }
        }
        @keyframes sfBlob2 {
          0%   { transform: translate(0,0) scale(1) rotate(0deg); }
          25%  { transform: translate(-50vw, -20vh) scale(1.5) rotate(-90deg); }
          50%  { transform: translate(60vw, 70vh) scale(0.75) rotate(-180deg); }
          75%  { transform: translate(30vw, -40vh) scale(1.25) rotate(-270deg); }
          100% { transform: translate(0,0) scale(1) rotate(-360deg); }
        }
        @keyframes sfBlob3 {
          0%   { transform: translate(0,0) scale(1) rotate(0deg); }
          33%  { transform: translate(55vw, 50vh) scale(1.2) rotate(120deg); }
          66%  { transform: translate(-40vw, -30vh) scale(0.9) rotate(240deg); }
          100% { transform: translate(0,0) scale(1) rotate(360deg); }
        }
        @keyframes sfBlob4 {
          0%   { transform: translate(0,0) scale(1) rotate(0deg); }
          30%  { transform: translate(-60vw, 40vh) scale(1.4) rotate(-108deg); }
          60%  { transform: translate(40vw, -50vh) scale(0.85) rotate(-216deg); }
          100% { transform: translate(0,0) scale(1) rotate(-360deg); }
        }
        @keyframes sfBlob5 {
          0%   { transform: translate(0,0) scale(1) rotate(0deg); }
          25%  { transform: translate(80vw, 20vh) scale(1.6) rotate(90deg); }
          50%  { transform: translate(-30vw, 90vh) scale(0.7) rotate(180deg); }
          75%  { transform: translate(20vw, -60vh) scale(1.3) rotate(270deg); }
          100% { transform: translate(0,0) scale(1) rotate(360deg); }
        }
        @keyframes sfBlob6 {
          0%   { transform: translate(0,0) scale(1) rotate(0deg); }
          40%  { transform: translate(-70vw, -40vh) scale(1.2) rotate(-144deg); }
          80%  { transform: translate(50vw, 60vh) scale(0.9) rotate(-288deg); }
          100% { transform: translate(0,0) scale(1) rotate(-360deg); }
        }

        .sf-blob {
          position: fixed;
          border-radius: 50%;
          /* Reduced from blur(100px) — same look at half the GPU cost */
          filter: blur(60px);
          opacity: ${themeMode === "dark" ? "0.18" : "0.28"};
          will-change: transform;
          pointer-events: none;
          z-index: 0;
          /* Force own composite layer so blob animations don't trigger
             repaints of unrelated content above */
          transform: translateZ(0);
          backface-visibility: hidden;
          /* Pause when the browser tab is in the background */
        }
        /* Pause every blob animation when the document is hidden, when the
           user prefers reduced motion, or on mobile (where the GPU cost is
           especially noticeable and the animation adds little). */
        body.sf-page-hidden .sf-blob,
        body.sf-page-hidden .ai-icon-ring { animation-play-state: paused !important; }
        @media (prefers-reduced-motion: reduce) {
          .sf-blob { animation: none !important; }
        }
        @media (max-width: 768px) {
          /* On mobile, drop to 3 blobs and freeze them — laptops/phones get
             very hot blurring 6 huge full-viewport circles 24/7. */
          .sf-blob-4, .sf-blob-5, .sf-blob-6 { display: none; }
          .sf-blob { animation: none !important; filter: blur(40px); }
        }
        .sf-blob-1 {
          width: 70vw; height: 70vw;
          left: 10vw; top: 5vh;
          background: ${themeMode === "dark"
            ? "radial-gradient(circle, #a78bfa 0%, #7c3aed 50%, transparent 100%)"
            : "radial-gradient(circle, #c8a8ff 0%, #b090f0 50%, transparent 100%)"};
          animation: sfBlob1 18s ease-in-out infinite;
        }
        .sf-blob-2 {
          width: 60vw; height: 60vw;
          right: 5vw; top: 20vh;
          background: ${themeMode === "dark"
            ? "radial-gradient(circle, #38bdf8 0%, #0ea5e9 50%, transparent 100%)"
            : "radial-gradient(circle, #90d0f8 0%, #70c0ee 50%, transparent 100%)"};
          animation: sfBlob2 22s ease-in-out infinite;
          opacity: ${themeMode === "dark" ? "0.15" : "0.24"};
        }
        .sf-blob-3 {
          width: 50vw; height: 50vw;
          left: 30vw; bottom: 15vh;
          background: ${themeMode === "dark"
            ? "radial-gradient(circle, #f472b6 0%, #ec4899 50%, transparent 100%)"
            : "radial-gradient(circle, #f8a0c0 0%, #f080b0 50%, transparent 100%)"};
          animation: sfBlob3 16s ease-in-out infinite;
          opacity: ${themeMode === "dark" ? "0.13" : "0.22"};
        }
        .sf-blob-4 {
          width: 55vw; height: 55vw;
          left: 5vw; bottom: 5vh;
          background: ${themeMode === "dark"
            ? "radial-gradient(circle, #34d399 0%, #059669 50%, transparent 100%)"
            : "radial-gradient(circle, #80f0c0 0%, #60e0a8 50%, transparent 100%)"};
          animation: sfBlob4 20s ease-in-out infinite;
          opacity: ${themeMode === "dark" ? "0.12" : "0.22"};
        }
        .sf-blob-5 {
          width: 45vw; height: 45vw;
          right: 20vw; top: 40vh;
          background: ${themeMode === "dark"
            ? "radial-gradient(circle, #fb923c 0%, #f97316 50%, transparent 100%)"
            : "radial-gradient(circle, #ffc080 0%, #ffaa60 50%, transparent 100%)"};
          animation: sfBlob5 14s ease-in-out infinite;
          opacity: ${themeMode === "dark" ? "0.16" : "0.26"};
        }
        .sf-blob-6 {
          width: 40vw; height: 40vw;
          left: 50vw; top: 10vh;
          background: ${themeMode === "dark"
            ? "radial-gradient(circle, #818cf8 0%, #4f46e5 50%, transparent 100%)"
            : "radial-gradient(circle, #c0a0f8 0%, #a888ee 50%, transparent 100%)"};
          animation: sfBlob6 25s ease-in-out infinite;
          opacity: ${themeMode === "dark" ? "0.14" : "0.22"};
        }

        * { box-sizing:border-box }
        body { margin:0 }
        .sf-editable-num { background:transparent !important; border:none !important; outline:none !important; -webkit-appearance:none !important; color:inherit; }
        .sf-editable-num::placeholder { color:inherit; opacity:1; }
        .sf-editable-num::-webkit-outer-spin-button, .sf-editable-num::-webkit-inner-spin-button { -webkit-appearance:none; margin:0; }
        .sf-editable-num[type=number] { -moz-appearance:textfield; }
        ::-webkit-scrollbar { width:0; height:0 }
        * { scrollbar-width:none }
        .sf-scroll::-webkit-scrollbar { width:4px }
        .sf-scroll::-webkit-scrollbar-thumb { background:rgba(139,92,246,0.25); border-radius:4px }

        /* ── Comprehensive Mobile Styles ── */
        @media (max-width: 768px) {
          /* Force single-column grids */
          .sf-profit-grid { grid-template-columns: 1fr !important; }

          /* Prevent iOS zoom on input focus — but not editable-num fields */
          input[type="number"]:not(.sf-editable-num), input[type="text"], input[type="email"], input[type="password"], select, textarea {
            font-size: 16px !important;
          }

          /* Tables scroll horizontally */
          table { display: block; overflow-x: auto; -webkit-overflow-scrolling: touch; }
          #ch-monthly-table { min-width: 500px !important; }

          /* Hide calculator on mobile */
          .sf-calculator-fab { display: none !important; }

          /* Page-level scroll isolation — prevent horizontal page scroll */
          .sf-root { overscroll-behavior-x: none; }
          .sf-root > main { overscroll-behavior: contain; touch-action: pan-y; overflow-x: hidden !important; }
          .sf-root > main > div { overflow-x: hidden !important; }

          /* Horizontal scroll containers — isolated from page scroll */
          .sf-hscroll { overflow-x: auto !important; -webkit-overflow-scrolling: touch; overscroll-behavior-x: contain; touch-action: pan-x; scroll-snap-type: x proximity; flex-wrap: nowrap !important; }
          .sf-hscroll::-webkit-scrollbar { display: none; }

          /* Settings/Profile full width */
          .sf-settings-wrap { max-width: 100% !important; margin: 0 !important; }

          /* Fix circles being oval */
          .sf-circle { border-radius: 50% !important; aspect-ratio: 1 / 1 !important; }

          /* Dropdown menus use fixed position on mobile */
          .sf-drop-mobile { position: fixed !important; top: auto !important; bottom: 0 !important; left: 0 !important; right: 0 !important; border-radius: 16px 16px 0 0 !important; max-height: 60vh !important; overflow-y: auto !important; animation: sfSlideUp 0.2s ease both !important; }

          /* Notification container — no page scroll bleed */
          .sf-notif-container { touch-action: none; overscroll-behavior: contain; }
        }
        @keyframes sfSlideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
    </div>
  );
}

// ─── Email verification screen ───────────────────────────────────────────────
function WelcomeScreen({ onClose }) {
  const T = useTheme();
  return (
    <div style={{
      position:"fixed", inset:0, zIndex:10000,
      background:T.bg, fontFamily:FONT,
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:20, overflowY:"auto",
    }}>
      <style>{`
        @keyframes welcOrb1 { 0%,100%{transform:translate(-50%,-50%) scale(1);opacity:0.15} 50%{transform:translate(-40%,-60%) scale(1.3);opacity:0.25} }
        @keyframes welcOrb2 { 0%,100%{transform:translate(50%,40%) scale(1);opacity:0.1} 50%{transform:translate(40%,30%) scale(1.4);opacity:0.2} }
        @keyframes welcOrb3 { 0%,100%{transform:translate(-20%,60%) scale(0.8);opacity:0.08} 50%{transform:translate(10%,50%) scale(1.2);opacity:0.18} }
        @keyframes welcShimmer { 0%{background-position:200% center} 100%{background-position:-200% center} }
        @keyframes welcFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        @keyframes welcFadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        @keyframes welcScaleIn { from{opacity:0;transform:scale(0.85)} to{opacity:1;transform:scale(1)} }
        @keyframes welcPulse { 0%,100%{box-shadow:0 0 0 0 rgba(139,92,246,0.4)} 50%{box-shadow:0 0 0 12px rgba(139,92,246,0)} }
        @keyframes welcStars { 0%{opacity:0} 20%{opacity:1} 80%{opacity:1} 100%{opacity:0;transform:translateY(-40px) scale(0.5)} }
        @keyframes welcGradientFlow { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
      `}</style>

      {/* Animated orbs — slow floating background lights */}
      <div style={{ position:"absolute", top:"20%", left:"30%", width:"40vw", height:"40vw", borderRadius:"50%",
        background:"radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
        pointerEvents:"none", filter:"blur(100px)", animation:"welcOrb1 8s ease-in-out infinite",
      }}/>
      <div style={{ position:"absolute", top:"40%", right:"20%", width:"35vw", height:"35vw", borderRadius:"50%",
        background:"radial-gradient(circle, rgba(6,182,212,0.25) 0%, transparent 70%)",
        pointerEvents:"none", filter:"blur(100px)", animation:"welcOrb2 10s ease-in-out infinite",
      }}/>
      <div style={{ position:"absolute", bottom:"10%", left:"40%", width:"30vw", height:"30vw", borderRadius:"50%",
        background:"radial-gradient(circle, rgba(139,92,246,0.15) 0%, rgba(236,72,153,0.1) 50%, transparent 70%)",
        pointerEvents:"none", filter:"blur(100px)", animation:"welcOrb3 12s ease-in-out infinite",
      }}/>

      {/* Floating sparkles */}
      {[...Array(6)].map((_, i) => (
        <div key={i} style={{
          position:"absolute", pointerEvents:"none", fontSize:["✨","💫","⭐","🌟","✦","✧"][i],
          top:`${15 + i * 12}%`, left:`${10 + (i * 17) % 80}%`,
          animation:`welcStars ${3 + i * 0.5}s ease-in-out ${i * 0.4}s infinite`,
          opacity:0,
        }}>{["✨","💫","⭐","🌟","✦","✧"][i]}</div>
      ))}

      <div style={{
        position:"relative", zIndex:1, width:"100%", maxWidth:540,
        animation:"welcScaleIn 0.6s cubic-bezier(0.34,1.3,0.64,1) both",
      }}>
        {/* Logo — floating animation */}
        <div style={{ textAlign:"center", marginBottom:32, animation:"welcFloat 4s ease-in-out infinite" }}>
          <div style={{
            display:"inline-block",
            background:"linear-gradient(135deg,#8b5cf6 0%,#7c3aed 50%,#06b6d4 100%)",
            backgroundSize:"200% 200%",
            animation:"welcGradientFlow 4s ease infinite, welcPulse 3s ease-in-out infinite",
            padding:"14px 28px", borderRadius:16,
            boxShadow:"0 12px 40px rgba(139,92,246,0.4)",
          }}>
            <span style={{ color:"#fff", fontSize:22, fontWeight:800, letterSpacing:-0.5, display:"inline-flex", alignItems:"center", gap:10 }}>
              <BrandLogo size={28} />
              CashHorizon&#8482;
            </span>
          </div>
        </div>

        {/* Main card */}
        <div style={{
          background:`linear-gradient(180deg, ${T.card} 0%, ${T.surface || T.card} 100%)`,
          border:`1px solid rgba(139,92,246,0.15)`,
          borderRadius:20, overflow:"hidden",
          boxShadow:`0 16px 64px rgba(0,0,0,0.4), 0 0 0 1px rgba(139,92,246,0.08)`,
        }}>
          {/* Purple accent stripe */}
          <div style={{ height:4, background:"linear-gradient(90deg,#8b5cf6,#06b6d4,#8b5cf6)" }}/>

          <div style={{ padding:"40px 36px 36px" }}>
            {/* Greeting — staggered animations */}
            <div style={{ textAlign:"center", marginBottom:28, animation:"welcFadeUp 0.6s ease both 0.2s" }}>
              <div style={{ fontSize:48, marginBottom:12, animation:"welcFloat 3s ease-in-out infinite" }}>👋</div>
              <h1 style={{
                fontSize:30, fontWeight:800, letterSpacing:-0.5, margin:"0 0 8px",
                background:"linear-gradient(90deg,#f0f2ff 0%,#a78bfa 25%,#06b6d4 50%,#a78bfa 75%,#f0f2ff 100%)",
                backgroundSize:"400% 100%",
                WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text",
                animation:"welcShimmer 6s linear infinite",
              }}>Welcome back!</h1>
              <p style={{ color:T.textSub, fontSize:15, lineHeight:1.6, margin:0 }}>
                It's great to see you again.
              </p>
            </div>

            {/* Community note */}
            <div style={{
              background:"rgba(139,92,246,0.06)", border:"1px solid rgba(139,92,246,0.15)",
              borderRadius:14, padding:"16px 20px", marginBottom:24,
              animation:"welcFadeUp 0.6s ease both 0.4s",
            }}>
              <p style={{ color:T.textSub, fontSize:13, lineHeight:1.7, margin:0 }}>
                Our team has been working around the clock to keep up with our rapidly growing community. To stay ahead of the massive demand, we're rolling out updates faster than ever!
              </p>
            </div>

            {/* What's New section */}
            <div style={{ marginBottom:28, animation:"welcFadeUp 0.6s ease both 0.6s" }}>
              <div style={{
                display:"inline-block", background:"rgba(6,182,212,0.1)", color:"#06b6d4",
                fontSize:11, fontWeight:700, padding:"4px 14px", borderRadius:20,
                letterSpacing:0.8, textTransform:"uppercase",
                border:"1px solid rgba(6,182,212,0.2)", marginBottom:16,
              }}>What's New</div>

              {/* Feature cards */}
              <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                <div style={{
                  background:T.surface || "rgba(13,15,26,0.6)", borderRadius:12,
                  padding:"16px 20px", border:`1px solid ${T.border}`,
                  display:"flex", gap:14, alignItems:"flex-start",
                }}>
                  <div style={{
                    width:36, height:36, borderRadius:10, flexShrink:0,
                    background:"linear-gradient(135deg,#8b5cf6,#7c3aed)",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:16, boxShadow:"0 4px 12px rgba(139,92,246,0.3)",
                  }}>🚀</div>
                  <div>
                    <div style={{ fontSize:14, fontWeight:700, color:T.text, marginBottom:4 }}>Fresh Features</div>
                    <div style={{ fontSize:13, color:T.textSub, lineHeight:1.6 }}>
                      We've just added several tools based on your feedback. Head over to <strong style={{ color:"#8b5cf6" }}>Targets</strong> and check out the new <strong style={{ color:"#06b6d4" }}>"What If"</strong> button to see them in action.
                    </div>
                  </div>
                </div>

                <div style={{
                  background:T.surface || "rgba(13,15,26,0.6)", borderRadius:12,
                  padding:"16px 20px", border:`1px solid ${T.border}`,
                  display:"flex", gap:14, alignItems:"flex-start",
                }}>
                  <div style={{
                    width:36, height:36, borderRadius:10, flexShrink:0,
                    background:"linear-gradient(135deg,#FF9500,#FF6B00)",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:16, boxShadow:"0 4px 12px rgba(255,149,0,0.3)",
                  }}>🔗</div>
                  <div>
                    <div style={{ fontSize:14, fontWeight:700, color:T.text, marginBottom:4 }}>Amazon API Update</div>
                    <div style={{ fontSize:13, color:T.textSub, lineHeight:1.6 }}>
                      We hear you! Due to tremendous demand and thousands of requests, our developers are fast-tracking the Amazon API integration. We are in the final stages and will announce the official release soon. Until then, please use the manual fields.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Community line */}
            <div style={{ textAlign:"center", marginBottom:24, animation:"welcFadeUp 0.6s ease both 0.8s" }}>
              <p style={{ color:T.textMuted || T.textSub, fontSize:12, lineHeight:1.6, margin:0, fontStyle:"italic" }}>
                Thanks for being part of our thriving community — we're building this together!
              </p>
            </div>

            {/* CTA */}
            <div style={{ animation:"welcFadeUp 0.6s ease both 1s" }}>
              <button onClick={onClose} style={{
                width:"100%", padding:"15px",
                background:"linear-gradient(135deg,#8b5cf6,#06b6d4)",
                backgroundSize:"200% 200%",
                animation:"welcGradientFlow 3s ease infinite",
                color:"#fff", border:"none", borderRadius:12,
                fontSize:16, fontWeight:700, fontFamily:FONT,
                cursor:"pointer",
                boxShadow:"0 8px 28px rgba(139,92,246,0.35)",
                transition:"transform 0.15s ease, box-shadow 0.15s ease",
              }}
              onMouseEnter={e=>{e.currentTarget.style.transform="scale(1.02)";e.currentTarget.style.boxShadow="0 12px 40px rgba(139,92,246,0.5)";}}
              onMouseLeave={e=>{e.currentTarget.style.transform="scale(1)";e.currentTarget.style.boxShadow="0 8px 28px rgba(139,92,246,0.35)";}}>
                Let's Go →
              </button>
            </div>

            {/* Tour hint */}
            <div style={{ textAlign:"center", marginTop:14, animation:"welcFadeUp 0.6s ease both 1.2s" }}>
              <p style={{ color:T.textMuted || T.textSub, fontSize:11, margin:0 }}>
                💡 Tip: Use the <strong style={{ color:T.text }}>Tour</strong> button in the navbar anytime for a guided walkthrough
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordScreen({ onDone }) {
  const T = useTheme();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit() {
    setError("");
    if (pw.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (pw !== pw2) { setError("Passwords don't match."); return; }
    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password: pw });
    setLoading(false);
    if (err) { setError(err.message); return; }
    setDone(true);
    setTimeout(() => onDone(), 2000);
  }

  const inp = { background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.md, color:T.text, padding:"12px 14px", fontSize:14, fontFamily:FONT, width:"100%", outline:"none", boxSizing:"border-box" };

  return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:FONT, padding:20 }}>
      <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg, padding:"40px 36px", width:"100%", maxWidth:400, boxShadow:T.shadowLg, textAlign:"center" }}>
        <div style={{ fontSize:48, marginBottom:16 }}>🔐</div>
        <div style={{ fontSize:22, fontWeight:800, color:T.text, marginBottom:8 }}>Set new password</div>
        <div style={{ fontSize:14, color:T.textSub, lineHeight:1.6, marginBottom:24 }}>
          Choose a new password for your account.
        </div>
        {done ? (
          <div style={{ color:T.green, fontSize:15, fontWeight:700 }}>✓ Password updated! Redirecting...</div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            <input type="password" placeholder="New password" value={pw} onChange={e=>setPw(e.target.value)} style={inp} autoFocus />
            <input type="password" placeholder="Confirm password" value={pw2} onChange={e=>setPw2(e.target.value)} style={inp}
              onKeyDown={e=>{ if (e.key==="Enter") handleSubmit(); }} />
            {error && <div style={{ color:T.red, fontSize:13 }}>{error}</div>}
            <button onClick={handleSubmit} disabled={loading} style={{
              background:T.accent, color:"#fff", border:"none",
              borderRadius:RADIUS.md, padding:"12px 24px", fontSize:14, fontWeight:700,
              fontFamily:FONT, cursor:loading?"wait":"pointer", width:"100%", opacity:loading?0.7:1,
            }}>{loading ? "Updating..." : "Update Password"}</button>
            <button onClick={onDone} style={{
              background:"none", border:"none", color:T.textSub, fontSize:12,
              fontFamily:FONT, cursor:"pointer", marginTop:4,
            }}>Skip — I'll change it later</button>
          </div>
        )}
      </div>
    </div>
  );
}

function EmailVerificationScreen({ email }) {
  const T = useTheme();
  const [resent, setResent] = useState(false);
  return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:FONT }}>
      <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg, padding:"40px 36px", width:"100%", maxWidth:400, boxShadow:T.shadowLg, textAlign:"center" }}>
        <div style={{ fontSize:48, marginBottom:16 }}>📧</div>
        <div style={{ fontSize:22, fontWeight:800, color:T.text, marginBottom:8 }}>Verify your email</div>
        <div style={{ fontSize:14, color:T.textSub, lineHeight:1.6, marginBottom:24 }}>
          We sent a verification link to <strong style={{ color:T.text }}>{email}</strong>. Click the link in the email to activate your account.
        </div>
        <button onClick={async () => {
          await supabase.auth.resend({ type:"signup", email });
          setResent(true);
          setTimeout(() => setResent(false), 5000);
        }} disabled={resent} style={{
          background:resent ? T.green : T.accent, color:"#fff", border:"none",
          borderRadius:RADIUS.md, padding:"11px 24px", fontSize:14, fontWeight:700,
          fontFamily:FONT, cursor:resent?"default":"pointer", width:"100%",
        }}>{resent ? "✓ Email resent!" : "Resend verification email"}</button>
        <button onClick={() => supabase.auth.signOut()} style={{
          background:"none", border:"none", color:T.textSub, fontSize:12,
          fontFamily:FONT, cursor:"pointer", marginTop:16, display:"block", width:"100%",
        }}>Sign out</button>
      </div>
    </div>
  );
}

// ─── Session expired screen ──────────────────────────────────────────────────
function SessionExpiredScreen() {
  const T = useTheme();
  return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:FONT }}>
      <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg, padding:"40px 36px", width:"100%", maxWidth:400, boxShadow:T.shadowLg, textAlign:"center" }}>
        <div style={{ fontSize:48, marginBottom:16 }}>🔒</div>
        <div style={{ fontSize:22, fontWeight:800, color:T.text, marginBottom:8 }}>Session expired</div>
        <div style={{ fontSize:14, color:T.textSub, lineHeight:1.6, marginBottom:24 }}>
          Your session has expired for security. Please sign in again — your data is safe.
        </div>
        <button onClick={() => window.location.reload()} style={{
          background:"linear-gradient(135deg,#8b5cf6,#06b6d4)", color:"#fff", border:"none",
          borderRadius:RADIUS.md, padding:"13px 24px", fontSize:15, fontWeight:700,
          fontFamily:FONT, cursor:"pointer", width:"100%",
          boxShadow:"0 8px 24px rgba(139,92,246,0.35)",
        }}>Sign In Again</button>
      </div>
    </div>
  );
}

// ─── Public Legal page (no auth, no app nav) ──────────────────────────────────
function PublicLegalPage() {
  const T = useTheme();
  const [, setTick] = useState(0);
  useEffect(() => {
    const onHash = () => setTick(t => t + 1);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const raw = window.location.hash.replace(/^#/, "");
  const params = {};
  raw.split("?").slice(1).join("?").split("&").forEach(kv => {
    const [k, v] = kv.split("=");
    if (k) params[decodeURIComponent(k)] = decodeURIComponent(v || "");
  });
  return (
    <div style={{ minHeight:"100vh", background:T.bg, fontFamily:FONT, color:T.text }}>
      <header style={{
        display:"flex", alignItems:"center", justifyContent:"space-between",
        padding:"14px 22px", borderBottom:`1px solid ${T.border}`,
        position:"sticky", top:0, background:T.bg, zIndex:10,
      }}>
        <a href="https://cashhorizon.io" style={{
          display:"flex", alignItems:"center", gap:10, textDecoration:"none", color:T.text, fontWeight:800, fontSize:15,
        }}>
          <BrandLogo size={28} style={{ borderRadius:8, display:"block" }} />
          CashHorizon&#8482;
        </a>
        <a href="https://cashhorizon.io" style={{
          fontSize:13, color:T.textSub, textDecoration:"none", fontWeight:600,
        }}>← Back to site</a>
      </header>
      <div style={{ padding:"32px 22px" }}>
        <Suspense fallback={<div style={{ padding:40, textAlign:"center", color:T.textSub }}>Loading…</div>}>
          <Legal navContext={params}/>
        </Suspense>
      </div>
    </div>
  );
}

// ─── Auth + subscription gate ─────────────────────────────────────────────────
function Root() {
  const { session, authLoading, subLoading, subscription, signOut, passwordRecovery, setPasswordRecovery } = useAuth();
  const [sessionExpired, setSessionExpired] = useState(false);
  const [inviteChecked, setInviteChecked] = useState(false);
  const [isTeamMember, setIsTeamMember] = useState(false);

  // Shopify-install follow-up. If the seller arrived via a Shopify install
  // link (LoginScreen persisted `ch.shopifyInstall` in sessionStorage on
  // first load), auto-navigate them to the Marketplaces page once they're
  // signed in AND have an active subscription — so they can finish the
  // Connect-API step rather than landing on the generic dashboard and
  // hunting for it. One-shot: the flag is cleared on redirect.
  useEffect(() => {
    try {
      const pending = sessionStorage.getItem("ch.shopifyInstall");
      if (!pending || !session) return;
      const active = subscription?.status === "active" || subscription?.status === "trialing";
      if (!active) return;
      const currentHash = (window.location.hash || "").replace(/^#/, "").split("?")[0];
      if (currentHash === "marketplaces") return; // already there
      sessionStorage.removeItem("ch.shopifyInstall");
      window.location.hash = `#marketplaces?shopify_install=${encodeURIComponent(pending)}`;
    } catch {}
  }, [session, subscription?.status]);

  // Public legal page — no auth, no app shell
  const hashPage = (typeof window !== "undefined" ? window.location.hash : "").replace(/^#/, "").split("?")[0];
  if (hashPage === "legal") return <PublicLegalPage/>;

  // Detect session expiration mid-use
  useEffect(() => {
    if (!session) return;
    const interval = setInterval(async () => {
      const { data: { session: current } } = await supabase.auth.getSession();
      if (!current && session) setSessionExpired(true);
    }, 60000);
    return () => clearInterval(interval);
  }, [session]);

  const [ownerSubStatus, setOwnerSubStatus] = useState(null); // null | "active" | "inactive" | etc.

  // Auto-accept pending invite on login (team members skip paywall)
  useEffect(() => {
    if (!session?.access_token || inviteChecked) return;
    (async () => {
      try {
        const res = await fetch("/api/account", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ action: "accept" }),
        });
        if (res.ok) { setIsTeamMember(true); }
        // Always fetch GET /api/account so we get ownerSubscriptionStatus too
        const checkRes = await fetch("/api/account", {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (checkRes.ok) {
          const data = await checkRes.json();
          if (data.account && !data.isOwner) {
            setIsTeamMember(true);
            setOwnerSubStatus(data.ownerSubscriptionStatus || "inactive");
          }
        }
      } catch {}
      setInviteChecked(true);
    })();
  }, [session?.access_token, inviteChecked]);

  if (sessionExpired) return <SessionExpiredScreen/>;
  if (authLoading)  return <LoadingScreen/>;
  if (!session)     return <LoginScreen/>;
  if (passwordRecovery) return <ResetPasswordScreen onDone={() => setPasswordRecovery(false)} />;

  const emailVerified = session.user?.email_confirmed_at || session.user?.confirmed_at;
  if (!emailVerified) return <EmailVerificationScreen email={session.user?.email}/>;

  if (subLoading || !inviteChecked) return <LoadingScreen/>;
  const isActive = subscription?.status==="active" || subscription?.status==="trialing";
  // Team members are gated on their OWNER's subscription status — not their own.
  // If the owner's account is paused, the team member sees the paused screen too.
  const ownerIsActive = ownerSubStatus === "active" || ownerSubStatus === "trialing";
  if (isTeamMember && !ownerIsActive) {
    return <PaywallTiers session={session} mode="team-member-paused"/>;
  }
  if (!isActive && !isTeamMember) return <PaywallTiers session={session}/>;
  return <AppShell session={session} subscription={subscription}/>;
}

// ─── Entry point ──────────────────────────────────────────────────────────────
export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <Root/>
        <CookieConsent/>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
